import { observable, makeAutoObservable, action, runInAction } from "mobx";
import { createContext } from "react";
import axios from "axios";
import { message } from "antd";
import { parse } from "query-string";

import { showErrorNotification } from "../utils/notification";
import {
  Coords,
  CustomerProfileSummary,
  IntervalGamePopulated,
  MomentResponseWithSize,
  Section,
  TeamAdsWithId,
  TeamPopupClickAction,
  TeamPopupConfigPopulated,
  TeamPopupItemPopulated,
  TeamPopupOccurrence,
  UniqueUser,
  EnhancedGeneralAdmissionTeamSettings,
} from "../shared/interfaces";
import type { TeamPopulated } from "../shared/interfaces/team";
import type { TeamGamePopulated } from "../shared/interfaces/game";
import { isMlbLeagueName, leagueNamesFromTeamLeague } from "../utils/isMlbTeam";
import { sanitizeAnalyticsPopupConfig } from "../utils/analyticsPopupConfig";

import { AppServiceInstance } from "./app";
import { getGameDataByIdRequest, getTeamAdsByTeamId, getTeamPopupsByTeamId } from "../axios/routes/game";
import {
  addUserRequest,
  getLastSession,
  updateRegistrationSessionRequest,
} from "../axios/routes/user";
import { cognitoSignUp } from "./cognito-auth";
import { Routes } from "../utils/routes";
import {
  StorageCropData, StorageSelectedMomentosData,
  ShownTipsData, SelectedMomentosData,
} from "../interfaces/moment";

import LogoIcon from "../assets/images/LogoHeader.png";
import { COLORS } from "../components/styled";
import { getAllSections, getOptedOutSeatsRequest } from "../axios/routes/moment";
import { getSuiteName, prepareSectionRowSeat } from "../utils/suite";
import {
  ASPECT_RATIO_LIST,
  ImageCropData,
  ImageLoadResult,
  loadImage,
  releaseCanvas,
} from "../utils/sharedFunctions";
import { LastPlace } from "../utils/lastPlaces";
import { clearMomentoSiteData } from "../utils/clearMomentoSiteData";
import { clearStaleCameraForSeatContext } from "./moment-seat-clear-hook";
import {
  computeFanFlowRequiresTeamPickerSync,
  getFlowPreviewHost,
} from "../utils/fanFlowNavigation";
import { isMobileDevice } from "../utils/device";
import { gameHasPublishedMoments } from "../utils/gamePublishedMoments";
import { isUnder18FlowTeenSession } from "../utils/welcomeAgeGate";

type FetchGameDataMode = "core" | "full";

function homeTeamAllowsFanScoreboard(game: IntervalGamePopulated | null | undefined): boolean {
  if (!game?.team1) return false;
  return game.team1.momentScoreboardFanEnabled !== false;
}

/** localStorage key for crop data fallback (Safari Cache API can fail to persist) */
const CROP_DATA_LOCALSTORAGE_KEY = "momento_imageCropData";

enum SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM {
  GAME="/selectedGame",
  SECTION="/selectedSection",
  SPECIAL_SECTION="/selectedSpecialSection",
  ROW="/selectedRow",
  SEAT="/selectedSeat",
  SUITE="/selectedSuite",
  VERIFY_LAST_NAME="/selectedVerifyLastName",
  FIRST_NAME="/selectedFirstName",
  LAST_NAME="/selectedLastName",
  EMAIL="/selectedEmail",
  CUSTOMER_ID="/selectedCustomerId",
  REGISTRATION_USER_ID="/selectedRegistrationUserId",
  /** game|section|row|seat fingerprint for the active registration session. */
  REGISTRATION_SESSION_KEY="/selectedRegistrationSessionKey",
  PHONE="/selectedPhone",
  ZIP_CODE="/selectedZipCode",
  BIRTHDATE="/selectedBirthDate",
  COUNTRY="/selectedCountry",
  TEAM_OPT_IN="/selectedTeamOptIn",
  SPONSOR_OPT_IN="/selectedSponsorOptIn",
  THIRD_OPT_IN="/selectedThirdOptIn",
  MOMENT="/selectedMoment",
  SELECTED_MOMENTO="/selectedSlide",
  CROP_DATA="/imageCropData",
  SHOWN_TIPS="/shownTips",
  SELECTED_MOMENTOS="/selectedImages",
  SELECTED_PLACE_DATA="/selectedPlaceData",
  SHOW_MOMENT_CAPTION="/selectedShowMomentCaption",
  SHOW_MOMENT_CAPTION_BY_GAME="/showMomentCaptionByGame",
  SECTION_IS_SPECIAL="/isSpecialSection",
  SECTION_TYPE="/specialType",
  PLACE_X="/selectedPlaceX",
  PLACE_Y="/selectedPlaceY",
  PLACE_WIDTH="/selectedPlaceWidth",
  PLACE_HEIGHT="/selectedPlaceHeight",
  PLACE_CENTER_X="/gaPlaceCenterX",
  PLACE_CENTER_Y="/gaPlaceCenterY",
  GA_SLICE_X="/gaPersistedSliceX",
  GA_SLICE_Y="/gaPersistedSliceY",
  GA_SLICE_W="/gaPersistedSliceW",
  GA_SLICE_H="/gaPersistedSliceH",
  ENHANCED_GA_SESSION="/enhancedGaSessionActive",
  ENHANCED_GA_SUB="/enhancedGaSubsectionIndex",
  LAST_PLACES="/lastPlaces",
  RETURNING_FAN_AUTHENTICATED="/returningFanAuthenticated",
}

export const DEFAULT_SIZE = 500;
export const MAX_SPECIAL_SECTION_WIDTH = 4000;

class StorageService {
  private workerFetchPromises = new Map<SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM, Promise<void>>();

  private fetchGameDataPromises = new Map<string, Promise<boolean>>();

  private sectionsFetchPromise: Promise<void> | null = null;

  private teamsFetchPromise: Promise<void> | null = null;

  private deferredTeamsTimer: ReturnType<typeof setTimeout> | null = null;

  @observable selectGameInProgress = false;

  /** True when selectedGameData came from fanLite (templates/ads not signed yet). */
  @observable gameDataIsFanLite = false;

  /** Avoid clearing `specialSections` on same-game refetch so GA slice math survives /my_moments refresh. */
  private lastSpecialSectionsGameId: string | null = null;

  @observable selectedGame: string = null;

  @observable selectedGameData: IntervalGamePopulated = null;

  /** True after game pick when home team uses the enhanced GA flow (until cleared). */
  @observable enhancedGaSessionActive = false;

  /** True when fan chose “Normal seat” on the enhanced GA entry screen (affects back navigation). */
  @observable enhancedGaNormalSeatFromFlow = false;

  /** Index into `team1.enhancedGeneralAdmission.subsections` for map / section pick. */
  @observable enhancedGaSubsectionIndex = 0;

  @observable specialSections: Section[] = [];

  @observable allSuites: Section[] = [];

  @observable specialSectionsListLoading = true;

  @observable selectedSection: string = null;

  @observable selectedSpecialSection: string = null;

  @observable selectedRow: string = null;

  @observable selectedSeat: string = null;

  @observable selectedSuite: string = null;

  @observable showSuites = true;

  @observable selectedSectionIsSpecial: boolean = null;

  @observable selectedSectionType: string = null;

  @observable selectedPlaceX: number = null;

  @observable selectedPlaceY: number = null;

  @observable selectedPlaceWidth: number = null;

  @observable selectedPlaceHeight: number = null;

  /** Hydration: PLACE_CENTER_X may arrive before PLACE_CENTER_Y from worker storage. */
  private pendingGaPlaceCenterX: number | null = null;

  /** Canonical fan position in Vision section-image space (0…sectionWidth), GA only. */
  @observable gaPlaceCenterSectionX: number | null = null;

  @observable gaPlaceCenterSectionY: number | null = null;

  /** Last successful GA getGameMoments slice window (global Vision coords). */
  @observable gaPersistedSliceX: number | null = null;

  @observable gaPersistedSliceY: number | null = null;

  @observable gaPersistedSliceW: number | null = null;

  @observable gaPersistedSliceH: number | null = null;

  /**
   * When the page URL carries GA placement (placeCx/slice*), worker hydration must not
   * overwrite it — async worker responses often arrive after the 2nd refresh and stomp URL restore.
   */
  private shouldApplyWorkerGaPlacement = (): boolean => {
    if (typeof window === "undefined") {
      return true;
    }
    const parsed = parse(window.location.search);
    const { section } = parsed;
    if (typeof section !== "string" || !section.startsWith("Special: ")) {
      return true;
    }
    const rawCx = parsed.placeCx;
    const rawCy = parsed.placeCy;
    if (
      rawCx !== undefined
      && rawCx !== null
      && String(rawCx) !== ""
      && rawCy !== undefined
      && rawCy !== null
      && String(rawCy) !== ""
    ) {
      const cx = parseFloat(String(rawCx));
      const cy = parseFloat(String(rawCy));
      if (Number.isFinite(cx) && Number.isFinite(cy)) {
        return false;
      }
    }
    const rawSw = parsed.sliceW;
    const rawSh = parsed.sliceH;
    const rawSx = parsed.sliceX;
    const rawSy = parsed.sliceY;
    if (
      rawSx !== undefined
      && rawSy !== undefined
      && rawSw !== undefined
      && rawSh !== undefined
      && String(rawSw) !== ""
      && String(rawSh) !== ""
    ) {
      const sw = parseFloat(String(rawSw));
      const sh = parseFloat(String(rawSh));
      if (Number.isFinite(sw) && Number.isFinite(sh) && sw > 0 && sh > 0) {
        return false;
      }
    }
    return true;
  };

  @observable selectedFirstName: string = null;

  @observable selectedLastName: string = null;

  @observable selectedEmail: string = null;

  @observable selectedCustomerId: string = null;

  /** User (registration) document id for the current seat session; used for per-session moment view counts. */
  @observable selectedRegistrationUserId: string = null;

  /**
   * Fingerprint of game+seat bound to `selectedRegistrationUserId`.
   * When game or seat changes, a new User registration (session) is created; customerProfileId stays.
   */
  @observable registrationSessionKey: string = null;

  @observable selectedPhone: string = null;

  @observable selectedZipCode: string = null;

  @observable selectedBirthDate: string = null;

  @observable selectedCountry: string = null;

  @observable selectedShowMomentCaption = false;

  /** Per-game “scoreboard on photo” (Adjust Photo); keys are game `_id` strings. */
  @observable showMomentCaptionByGame: Record<string, boolean> = {};

  private legacySingleShowMomentCaptionHydrated = false;

  private legacySingleShowMomentCaptionValue = false;

  @observable storageCropData: StorageCropData = null;

  @observable storageSelectedPlaceData: StorageCropData = null;

  @observable storageSelectedMomentosData: StorageSelectedMomentosData = null;

  @observable selectedTeamOptIn: boolean = null;

  @observable selectedSponsorOptIn: boolean = null;

  @observable selectedThirdOptIn: boolean = null;

  /** Fan saw the watermark opt-in modal this registration (server-backed). */
  @observable selectedWatermarkOptInShown: boolean = null;

  /** Fan accepted the watermark opt-in modal this registration (server-backed). */
  @observable selectedWatermarkOptIn: boolean = null;

  @observable selectedIsSeasonTicketHolder = false;

  @observable isReturningFanAuthenticated = false;

  @observable returningFanPromptHandledForGame: string = null;

  @observable pendingFanPassword = "";

  // TODO: change to actual time
  @observable selectedMoment: string = null;

  @observable selectedMomento: number = null;

  @observable shownTipsData: ShownTipsData = null;

  @observable availableSections: Section[] = [];

  @observable stadiumMap: Map<string, Map<string, Set<string>>> = new Map();

  @observable sectionsListLoading = false;

  // cached team logos
  private teamLogosLoading = false;

  @observable team1logoImage: HTMLImageElement | null = null;

  @observable team1logoDataUrl: string | null = null;

  @observable team1logoSrc: string | null = null;

  @observable team2logoImage: HTMLImageElement | null = null;

  @observable team2logoDataUrl: string | null = null;

  @observable team2logoSrc: string | null = null;

  @observable mainLogoImage: HTMLImageElement | null = null;

  @observable team1TicketAdImage: HTMLImageElement | null = null;

  @observable team1TicketAdDataUrl: string | null = null;

  @observable team1TicketAdSrc: string | null = null;

  @observable team1PopupImage: HTMLImageElement | null = null;

  @observable team1PopupDataUrl: string | null = null;

  @observable team1PopupSrc: string | null = null;

  @observable showPopupImage = false;

  @observable popupImageWasShown = false;

  // team colors
  @observable team1Color: string | null = COLORS.GREEN_DARK;

  @observable team2Color: string | null = COLORS.GREY_DARK;

  // cached team Advertisments
  @observable teamAds: TeamAdsWithId | null = null;

  /** Operator-configured pop-ups for the home team (signed image URLs on items). */
  @observable teamPopupsConfig: TeamPopupConfigPopulated | null = null;

  /** Set when {@link loadTeamPopupsMetadata} finishes for the current team (success or error). */
  @observable teamPopupsFetchCompletedForTeamId: string | null = null;

  /** Active pop-up payload for the global modal (set by occurrence triggers). */
  @observable pendingTeamPopup: TeamPopupItemPopulated | null = null;

  /** My Moments registers the high-resolution purchase flow for "Direct to purchase" from a pop-up. */
  teamPopupDirectPurchaseHandler: (() => Promise<void>) | null = null;

  @observable team1SponsorLogoImage: HTMLImageElement | null = null;

  @observable team1SponsorLogoDataUrl: string | null = null;

  @observable team1SponsorLogoSrc: string | null = null;

  @observable showAdAfterContactInfo = true;

  @observable adAfterContactInfoBlob: Blob | null = null;

  @observable adAfterContactInfoSrc: string | null = null;

  // indicates that at least single team dictates to hide purchase
  @observable hidePurchaseActions = false;

  /** Home team: after first My Moments still save, show keepsake store popup. */
  @observable purchasePopupAfterDownloadedImage = false;

  @observable pendingKeepsakeDownloadPopup = false;

  /** Home team: after first photo save in a visit, show SHARE THE MOMENT. */
  @observable fanSharingPromptEnabled = false;

  @observable pendingFanSharingPrompt = false;

  @observable fanSharingPromptPrepared: {
    finalImageUrl: string;
    actualMimeType: string;
    uniqueFilename: string;
  } | null = null;

  /** Home team: after first photo save in a visit, show TELL US YOUR GAMEDAY STORY. */
  @observable gamedayStoryPromptEnabled = false;

  @observable pendingGamedayStoryPrompt = false;

  @observable hideDownloadActions = false;

  // indicates that home team should not show Show Order Printed Photo button
  @observable showOrderPrintedPhoto = false;

  // indicates that watermark should be shown
  @observable showWatermark = false;

  // indicates that paid download is enabled
  @observable enablePaidDownload = false;

  // last places information
  @observable lastPlaces: LastPlace[] = [];

  // store moments camera data to access it
  public defaultZoomWidth: number = null;

  public defaultZoomHeight: number = null;

  public centerCoords: Coords = null;

  public offsetY: number = null;

  @observable resumeSessionDialogWasShown = false;

  // verify place
  @observable selectedVerifyLastName: string = null;

  // we should show user details first (lead gen first flow)
  @observable showUserDetailsFirst = false;

  /** Welcome-page team config used before a game is selected (lead gen first). */
  @observable welcomeTeamContext: TeamGamePopulated | null = null;

  /** Hub / first-party hosts: /teams before games. Dedicated team hostname skips picker. */
  @observable fanFlowRequiresTeamPicker = false;

  /** Hub flow: filter games to home club (`team1.name` lowercased). */
  @observable hubSelectedHomeTeamName: string | null = null;

  /** Hub club ObjectId after /teams pick — scopes recent-games API. */
  @observable hubSelectedHomeTeamId: string | null = null;

  // we should show half of the image blurred
  @observable blurImage = false;

  /**
   * True when this game's home club (always `team1` on the game record) has the
   * Enhanced General Admission feature enabled in operator. After game select,
   * the fan should go to the "Where were you sitting?" step whenever this is true.
   */
  shouldUseEnhancedGeneralAdmissionFlow = (): boolean => {
    const eg = this.selectedGameData?.team1?.enhancedGeneralAdmission as
      EnhancedGeneralAdmissionTeamSettings | undefined;
    return eg?.enabled === true;
  };

  private persistEnhancedGaSessionToWorker = () => {
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ENHANCED_GA_SESSION,
      this.enhancedGaSessionActive ? "true" : "false",
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ENHANCED_GA_SUB,
      `${this.enhancedGaSubsectionIndex}`,
    );
  };

  @action clearEnhancedGaSession = () => {
    this.enhancedGaSessionActive = false;
    this.enhancedGaNormalSeatFromFlow = false;
    this.enhancedGaSubsectionIndex = 0;
    this.persistEnhancedGaSessionToWorker();
  };

  @action startEnhancedGaSession = () => {
    this.enhancedGaSessionActive = true;
    this.enhancedGaNormalSeatFromFlow = false;
    this.enhancedGaSubsectionIndex = 0;
    this.persistEnhancedGaSessionToWorker();
  };

  @action markEnhancedGaNormalSeatFromFlow = () => {
    this.enhancedGaNormalSeatFromFlow = true;
  };

  @action setEnhancedGaSubsectionIndex = (idx: number) => {
    this.enhancedGaSubsectionIndex = idx;
    this.persistEnhancedGaSessionToWorker();
  };

  constructor() {
    makeAutoObservable(this);

    const { hostname, search } = window.location;
    const hostnamePrefix = hostname?.substring(0, hostname.indexOf(".")).toLowerCase();
    this.blurImage = hostnamePrefix === "amex" || search?.indexOf("blurred=true") !== -1;
    this.fanFlowRequiresTeamPicker = getFlowPreviewHost()
      ? false
      : computeFanFlowRequiresTeamPickerSync(hostname);
    try {
      const rawWelcomeTeam = sessionStorage.getItem("momento_welcome_team_context");
      if (rawWelcomeTeam) {
        const parsed = JSON.parse(rawWelcomeTeam) as TeamGamePopulated;
        this.welcomeTeamContext = parsed;
        this.showUserDetailsFirst = parsed?.leadGenFirst === true;
        if (parsed?.primaryColor) {
          this.team1Color = parsed.primaryColor;
        }
      }
    } catch {
      /* ignore */
    }
    try {
      const hubTeam = sessionStorage.getItem("momento_hub_team_filter");
      if (hubTeam) this.hubSelectedHomeTeamName = hubTeam;
      const hubTeamId = sessionStorage.getItem("momento_hub_team_id");
      if (hubTeamId) this.hubSelectedHomeTeamId = hubTeamId;
    } catch {
      /* ignore */
    }

    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GAME);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPECIAL_SECTION);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ROW);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SEAT);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SUITE);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.FIRST_NAME);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.LAST_NAME);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.EMAIL);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_USER_ID);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_SESSION_KEY);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PHONE);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ZIP_CODE);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.BIRTHDATE);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.COUNTRY);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.TEAM_OPT_IN);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPONSOR_OPT_IN);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.THIRD_OPT_IN);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.THIRD_OPT_IN);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.VERIFY_LAST_NAME);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.MOMENT);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTO);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTOS);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_PLACE_DATA);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOWN_TIPS);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_IS_SPECIAL);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_TYPE);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_X);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_Y);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_X);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_Y);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_W);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_H);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_X);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_Y);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_WIDTH);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_HEIGHT);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ENHANCED_GA_SESSION);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ENHANCED_GA_SUB);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.LAST_PLACES);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION);
    this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION_BY_GAME);
  }

  private gameDataMatchesSelection = (gameId?: string): boolean => {
    const loadedId = this.selectedGameData?._id?.toString();
    const expectedId = (gameId || this.selectedGame)?.toString();
    return Boolean(loadedId && expectedId && loadedId === expectedId);
  };

  private syncTeamColorsFromSelectedGame = () => {
    runInAction(() => {
      this.team1Color = this.selectedGameData?.team1?.primaryColor || COLORS.GREEN_DARK;
      this.team2Color = this.selectedGameData?.team2?.primaryColor || COLORS.GREY_DARK;
    });
  };

  /** Persist MLB league onto welcome context when a game loads (covers stale sessionStorage). */
  private syncMlbLeagueFromSelectedGame = () => {
    if (!isMlbLeagueName(this.selectedGameData?.leagueName)) return;
    if (!this.welcomeTeamContext) return;
    const existing = this.welcomeTeamContext.leagueNames || [];
    if (existing.some(isMlbLeagueName)) return;
    runInAction(() => {
      this.welcomeTeamContext = {
        ...this.welcomeTeamContext,
        leagueNames: [...existing, "MLB"],
      };
      try {
        sessionStorage.setItem(
          "momento_welcome_team_context",
          JSON.stringify(this.welcomeTeamContext),
        );
      } catch {
        /* ignore */
      }
    });
  };

  /** Team ads + popups config (API only). */
  @action ensureTeamConfigLoaded = async (): Promise<void> => {
    const teamId = this.selectedGameData?.team1?._id ?? this.welcomeTeamContext?._id;
    if (!teamId) return;
    await this.loadTeamAdsMetadata(teamId);
    if (this.selectedGameData?.team1?._id) {
      await this.loadTeamPopupsMetadata();
    }
  };

  /** Header branding on lead-gen-first details (no game selected yet). */
  @action ensureWelcomeHeaderBrandAssets = async (): Promise<void> => {
    if (!this.welcomeTeamContext?._id) return;
    await this.loadTeamAdsMetadata(this.welcomeTeamContext._id);
    await this.loadWelcomeTeamHeaderLogo();
    await this.loadTeamAdsImages();
  };

  /** Header team/sponsor assets for seat, games, signup — not used on My Moments (URL fallbacks there). */
  @action ensureHeaderBrandAssets = async (): Promise<void> => {
    await this.ensureTeamConfigLoaded();
    await this.loadTeamAdsImages();
    if (!isMobileDevice()) {
      await this.loadCoreTeamLogos();
    }
  };

  cancelScheduledTeamVisualAssets = () => {
    if (this.deferredTeamsTimer) {
      clearTimeout(this.deferredTeamsTimer);
      this.deferredTeamsTimer = null;
    }
  };

  /**
   * Defer logo/sponsor canvas work until after route paint.
   * My Moments uses FanMomentScoreboardBar URL fallbacks — skip there to avoid Safari OOM.
   */
  scheduleTeamVisualAssets = (forGameId?: string, options: { force?: boolean } = {}) => {
    const gameId = (forGameId || this.selectedGame)?.toString();
    if (!gameId) return;

    const path = AppServiceInstance.history?.location?.pathname
      || (typeof window !== "undefined" ? window.location.pathname : "");
    if (!options.force && (path === Routes.MY_MOMENTS || path === Routes.ALL_MOMENTO)) {
      return;
    }

    this.cancelScheduledTeamVisualAssets();

    const delayMs = isMobileDevice() ? 4500 : 600;
    this.deferredTeamsTimer = setTimeout(() => {
      this.deferredTeamsTimer = null;
      if (this.selectedGame?.toString() !== gameId) return;
      const currentPath = AppServiceInstance.history?.location?.pathname
        || (typeof window !== "undefined" ? window.location.pathname : "");
      if (currentPath === Routes.MY_MOMENTS || currentPath === Routes.ALL_MOMENTO) {
        return;
      }
      this.fetchTeamVisualAssets({ skipClearCache: true }).catch((err) => {
        console.warn(`[fetchTeamVisualAssets] ${err?.message ?? err}`);
      });
    }, delayMs);
  };

  @action selectGame = async (game: IntervalGamePopulated) => {
    if (this.selectGameInProgress) return;

    this.selectGameInProgress = true;
    try {
      this.clearEnhancedGaSession();
      const prevGameId = this.selectedGame?.toString();
      this.selectedGame = game._id;
      this.isReturningFanAuthenticated = false;
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.RETURNING_FAN_AUTHENTICATED);
      this.returningFanPromptHandledForGame = null;

      const hasPublishedMoments = gameHasPublishedMoments(game);
      const gameId = game._id?.toString();

      if (prevGameId !== gameId) {
        this.cancelScheduledTeamVisualAssets();
        this.clearCachedImages();
      }
      this.showAdAfterContactInfo = true;

      const loaded = await this.fetchGameData(game._id, "core");

      if (!loaded || !this.gameDataMatchesSelection(game._id)) {
        showErrorNotification("Could not load this event. Please try again or pick another game.");
        if (this.selectedGame?.toString() === gameId) {
          this.selectedGame = null;
          this.selectedGameData = null;
          this.gameDataIsFanLite = false;
        }
        return;
      }

      this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GAME, this.selectedGame);

      // Switching games must start a new registration session (same email/profile, new User row).
      // Do not patch the previous game's registration with the new game id.
      // Drop seat so a new registration is created with this game + the new seat (opt-ins stay).
      if (prevGameId && gameId && prevGameId !== gameId && this.selectedRegistrationUserId) {
        this.clearActiveRegistrationSession({ keepCustomerProfile: true });
        this.clearSeatAndPlaceSelection();
      } else if (prevGameId && gameId && prevGameId !== gameId) {
        this.clearSeatAndPlaceSelection();
      }

      const useEnhancedGa = this.shouldUseEnhancedGeneralAdmissionFlow();

      runInAction(() => {
        if (hasPublishedMoments && !this.popupImageWasShown) {
          this.showPopupImage = true;
        }

        if (!hasPublishedMoments) {
          if (!this.popupImageWasShown) {
            this.showPopupImage = true;
          }
          this.clearEnhancedGaSession();
          AppServiceInstance.history.push(Routes.SIGNUP);
        } else if (useEnhancedGa) {
          if (!this.popupImageWasShown) {
            this.showPopupImage = true;
          }
          this.startEnhancedGaSession();
          AppServiceInstance.history.push(Routes.ENHANCED_GA_WHERE);
        } else {
          this.moveToTheNextPage(Routes.GAMES);
        }
        // Unblock games list UI as soon as we route; seat/EGA pages load extras themselves.
        this.selectGameInProgress = false;
      });

      if (gameId) {
        this.fetchSpecialSectionsData().catch((err) => {
          console.warn(`[fetchSpecialSectionsData] ${err?.message ?? err}`);
        });
      }

      this.ensureHeaderBrandAssets().catch((err) => {
        console.warn(`[ensureHeaderBrandAssets] ${err?.message ?? err}`);
      });

      if (gameId && !isMobileDevice()) {
        this.scheduleTeamVisualAssets(gameId);
      }
    } finally {
      this.selectGameInProgress = false;
    }
  };

  @action loadGameData = async (gameId: string) => {
    const nextId = gameId?.toString();
    const loadedId = this.selectedGameData?._id?.toString();
    if (nextId && nextId !== loadedId) {
      this.clearCachedImages();
    }

    await this.fetchGameData(gameId, "full");
  };

  /**
   * Ensure game payload includes ticket templates / signed template URLs.
   * Core select-game uses fanLite; call this before ticket download/editor.
   */
  @action ensureGameTemplatesLoaded = async (): Promise<boolean> => {
    if (!this.selectedGame) return false;
    if (
      this.gameDataMatchesSelection()
      && !this.gameDataIsFanLite
      && !!this.selectedGameData?.ticketTemplateImageKey
    ) {
      return true;
    }
    return this.fetchGameData(this.selectedGame, "full");
  };

  /** Reload game details when route guards need populated data (e.g. after a failed fetch). */
  @action ensureSelectedGameDataLoaded = async (): Promise<boolean> => {
    if (this.gameDataMatchesSelection()) return true;
    if (!this.selectedGame) return false;
    return this.fetchGameData(this.selectedGame, "core");
  };

  /** Seat page: load section list if navigation beat background extras. */
  @action ensureSeatSectionsLoaded = async (): Promise<boolean> => {
    if (!this.selectedGameData?._id && this.selectedGame) {
      await this.ensureSelectedGameDataLoaded();
    }
    const gameId = this.selectedGameData?._id?.toString();
    if (!gameId || gameId !== this.selectedGame?.toString()) {
      return false;
    }
    if (this.lastSpecialSectionsGameId === gameId && this.availableSections.length > 0) {
      return true;
    }
    await this.fetchSpecialSectionsData();
    return (
      this.lastSpecialSectionsGameId === gameId
      && this.availableSections.length > 0
      && !this.specialSectionsListLoading
    );
  };

  /**
   * After a full page refresh on seat/details/etc., worker storage restores ids and
   * seat fields but not populated game data — reload game (and seat sections when needed).
   */
  @action restoreFanSessionFromWorker = async (options?: { loadSeatSections?: boolean }): Promise<boolean> => {
    await Promise.all([
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GAME),
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION),
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPECIAL_SECTION),
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ROW),
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SEAT),
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SUITE),
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_IS_SPECIAL),
      this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_TYPE),
    ]);
    if (!this.selectedGame) return false;
    const loaded = await this.ensureSelectedGameDataLoaded();
    if (!loaded) return false;
    if (options?.loadSeatSections) {
      await this.ensureSeatSectionsLoaded();
    }
    return true;
  };

  private bootstrapGameDataAfterWorkerHydration = (): void => {
    if (this.selectGameInProgress || !this.selectedGame) return;
    if (this.gameDataMatchesSelection()) return;
    this.ensureSelectedGameDataLoaded()
      .then((ok) => {
        if (!ok) return undefined;
        const path = AppServiceInstance.history?.location?.pathname
          || (typeof window !== "undefined" ? window.location.pathname : "");
        if (
          path === Routes.SEAT
          || path === Routes.ENHANCED_GA_WHERE
          || path === Routes.ENHANCED_GA_AREA
          || path === Routes.ENHANCED_GA_MAP
          || path === Routes.PLACE
        ) {
          return this.ensureSeatSectionsLoaded();
        }
        return undefined;
      })
      .catch(() => undefined);
  };

  @action loadGameWithSeat = async (gameId: string, section: string, row: string, seat: string) => {
    this.selectedGame = gameId;
    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GAME, this.selectedGame);
    await this.fetchGameData(gameId);

    if (section?.startsWith("Special: ")) {
      const sectionName = section.substring(9);
      this.saveUsersPlace(true, sectionName, "", "", "", "");
      const partsY = (row || "").split("-");
      const partsX = (seat || "").split("-");
      if (partsY.length > 1 && partsX.length > 1) {
        const placeX = Number(partsX[0]);
        const placeY = Number(partsY[0]);
        const placeW = Number(partsX[1]);
        const placeH = Number(partsY[1]);
        if ([placeX, placeY, placeW, placeH].every((n) => Number.isFinite(n))) {
          this.saveGaPlaceCenter(placeX + placeW / 2, placeY + placeH / 2);
        }
      }
    } else {
      const suite = getSuiteName(section);
      if (suite) {
        this.saveUsersPlace(false, "", "", "", this.showSuites ? suite : "");
      } else {
        this.saveUsersPlace(false, section, row, seat, "");
      }
    }
  };

  // update local storage
  @action updateSavedDataFromRestoredSession = (
    selectedGame: string,
    TeamOptIn: boolean,
    SponsorOptIn: boolean,
    customerId: string,
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    zipCode: string,
    birthDate: string,
    country: string,
    section: string,
    row: string,
    seat: string,
    saveUserDetails = true,
    options?: { skipGaPlacePinDecode?: boolean },
  ) => {
    this.selectedGame = selectedGame;
    this.selectedCustomerId = customerId || this.selectedCustomerId;
    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GAME, this.selectedGame);

    if (section?.startsWith("Special: ")) {
      const sectionName = section.substring(9);
      this.saveUsersPlace(true, sectionName, "", "", "", "");

      if (!options?.skipGaPlacePinDecode) {
        const partsY = (row || "").split("-");
        const partsX = (seat || "").split("-");

        if (partsY.length > 1 && partsX.length > 1) {
          const placeX = Number(partsX[0]);
          const placeY = Number(partsY[0]);
          const placeW = Number(partsX[1]);
          const placeH = Number(partsY[1]);
          if ([placeX, placeY, placeW, placeH].every((n) => Number.isFinite(n))) {
            this.saveGaPlaceCenter(placeX + placeW / 2, placeY + placeH / 2);
          }
        }
      }
    } else {
      const suite = getSuiteName(section);
      if (suite) {
        this.saveUsersPlace(false, "", "", "", this.showSuites ? suite : "");
      } else {
        this.saveUsersPlace(false, section, row, seat, "");
      }
    }

    if (saveUserDetails) {
      this.selectedFirstName = firstName;
      this.selectedLastName = lastName;
      this.selectedEmail = email;
      this.selectedPhone = phone;
      this.selectedZipCode = zipCode;
      this.selectedBirthDate = birthDate;
      this.selectedCountry = country;
      this.selectedTeamOptIn = TeamOptIn;
      this.selectedSponsorOptIn = SponsorOptIn;

      this.saveUserDetailsToStorage(TeamOptIn, SponsorOptIn, this.selectedThirdOptIn);
    }

    // use provided email for current session only if we do not have email
    if (!saveUserDetails && email && !this.selectedEmail) {
      this.selectedEmail = email;
    }

    if (!saveUserDetails && customerId && !this.selectedCustomerId) {
      this.selectedCustomerId = customerId;
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID,
        this.selectedCustomerId,
      );
    }
  }

  @action
  public hydrateFromWorkerStorage = async (): Promise<void> => {
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.FIRST_NAME);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.LAST_NAME);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.EMAIL);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_USER_ID);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_SESSION_KEY);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PHONE);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ZIP_CODE);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.BIRTHDATE);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.COUNTRY);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.TEAM_OPT_IN);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPONSOR_OPT_IN);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.THIRD_OPT_IN);
    await this.fetchDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.RETURNING_FAN_AUTHENTICATED);
  };

  @action public updateVerifyLastName = (lastName: string) => {
    this.selectedVerifyLastName = lastName;

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.VERIFY_LAST_NAME,
      this.selectedVerifyLastName,
    );
  }

  // update local storage
  @action public savePreviousSessionToStorage = (previousSession: UniqueUser) => {
    this.selectedRegistrationUserId = previousSession._id
      ? String(previousSession._id)
      : null;
    if (typeof previousSession.ThirdOptIn === "boolean") {
      this.selectedThirdOptIn = previousSession.ThirdOptIn;
    }
    this.updateSavedDataFromRestoredSession(
      previousSession.game,
      previousSession.TeamOptIn,
      previousSession.SponsorOptIn,
      previousSession.customerProfileId || previousSession._id,
      previousSession.firstName,
      previousSession.lastName,
      previousSession.email,
      previousSession.phoneNumber,
      previousSession.zipCode,
      previousSession.birthDate,
      previousSession.country,
      previousSession.section,
      previousSession.row,
      previousSession.seat,
    );
    this.bindRegistrationSessionKey(
      previousSession.game,
      previousSession.section,
      previousSession.row,
      previousSession.seat,
    );
  }

  /**
   * Fan declined "return to last session" on Welcome. Keep contact + Team/Sponsor opt-ins
   * for the next game/seat registration, but drop game, seat, and the previous User row.
   */
  @action public declinePreviousSessionResume = (previousSession: UniqueUser) => {
    if (!previousSession) return;

    if (previousSession.firstName) {
      this.selectedFirstName = previousSession.firstName;
    }
    if (previousSession.lastName) {
      this.selectedLastName = previousSession.lastName;
    }
    if (previousSession.email) {
      this.selectedEmail = previousSession.email;
    }
    if (previousSession.phoneNumber) {
      this.selectedPhone = previousSession.phoneNumber;
    }
    if (previousSession.zipCode) {
      this.selectedZipCode = previousSession.zipCode;
    }
    if (previousSession.birthDate) {
      this.selectedBirthDate = previousSession.birthDate;
    }
    if (previousSession.country) {
      this.selectedCountry = previousSession.country;
    }
    if (typeof previousSession.TeamOptIn === "boolean") {
      this.selectedTeamOptIn = previousSession.TeamOptIn;
    }
    if (typeof previousSession.SponsorOptIn === "boolean") {
      this.selectedSponsorOptIn = previousSession.SponsorOptIn;
    }
    if (typeof previousSession.ThirdOptIn === "boolean") {
      this.selectedThirdOptIn = previousSession.ThirdOptIn;
    }
    if (typeof previousSession.isSeasonTicketHolder === "boolean") {
      this.selectedIsSeasonTicketHolder = previousSession.isSeasonTicketHolder;
    }

    const profileId = previousSession.customerProfileId || previousSession._id;
    if (profileId) {
      this.selectedCustomerId = String(profileId);
    }

    this.saveUserDetailsToStorage(
      this.selectedTeamOptIn as boolean,
      this.selectedSponsorOptIn as boolean,
      this.selectedThirdOptIn as boolean,
    );

    this.clearActiveRegistrationSession({ keepCustomerProfile: true });
    this.clearSeatAndPlaceSelection();
    this.clearSelectedGameForResumeDecline();
  }

  /** Clear game loaded only to preview the last-session popup. */
  @action private clearSelectedGameForResumeDecline = () => {
    this.selectedGame = null;
    this.selectedGameData = null;
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GAME);
  };

  /** Clear seat/GA place so a new game does not reuse the previous session's location. */
  @action private clearSeatAndPlaceSelection = () => {
    this.selectedSection = null;
    this.selectedSpecialSection = null;
    this.selectedRow = null;
    this.selectedSeat = null;
    this.selectedSuite = null;
    this.selectedSectionIsSpecial = null;
    this.selectedSectionType = null;
    this.selectedPlaceX = null;
    this.selectedPlaceY = null;
    this.selectedPlaceWidth = null;
    this.selectedPlaceHeight = null;
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION);
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPECIAL_SECTION);
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ROW);
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SEAT);
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SUITE);
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_IS_SPECIAL);
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_TYPE);
    this.clearVerifyLastName();
  };

  @action public savePendingFanPassword = (password: string) => {
    this.pendingFanPassword = password;
  }

  @action public clearPendingFanPassword = () => {
    this.pendingFanPassword = "";
  }

  @action public setReturningFanAuthenticated = (value: boolean) => {
    this.isReturningFanAuthenticated = value;
    if (value) {
      this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.RETURNING_FAN_AUTHENTICATED, "true");
    } else {
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.RETURNING_FAN_AUTHENTICATED);
    }
  }

  @action public markReturningFanPromptHandled = (gameId?: string) => {
    this.returningFanPromptHandledForGame = gameId || this.selectedGame;
  }

  public shouldShowReturningFanPrompt = (): boolean => {
    if (!this.selectedGame) {
      return false;
    }
    if (this.selectedCustomerId) {
      return false;
    }
    if (this.selectedGameData?.team1?.profileEnabled === false) {
      return false;
    }
    return this.returningFanPromptHandledForGame !== this.selectedGame;
  }

  @action public applyCustomerProfileSummary = (
    profile?: CustomerProfileSummary,
  ) => {
    if (!profile) {
      return;
    }

    this.selectedCustomerId = profile._id || this.selectedCustomerId;
    this.selectedFirstName = profile.firstName || this.selectedFirstName;
    this.selectedLastName = profile.lastName || this.selectedLastName;
    this.selectedEmail = profile.email || this.selectedEmail;
    this.selectedPhone = profile.phoneNumber || this.selectedPhone;
    this.selectedZipCode = profile.zipCode || this.selectedZipCode;
    this.selectedBirthDate = profile.birthDate || this.selectedBirthDate;
    this.selectedCountry = profile.country || this.selectedCountry;
    this.selectedTeamOptIn = profile.TeamOptIn ?? this.selectedTeamOptIn;
    this.selectedSponsorOptIn = profile.SponsorOptIn ?? this.selectedSponsorOptIn;
    this.selectedThirdOptIn = profile.ThirdOptIn ?? this.selectedThirdOptIn;
    this.selectedIsSeasonTicketHolder = profile.isSeasonTicketHolder ?? this.selectedIsSeasonTicketHolder;
    this.saveUserDetailsToStorage(
      this.selectedTeamOptIn,
      this.selectedSponsorOptIn,
      this.selectedThirdOptIn,
    );
  }

  @action public applyProfileFromEmailLookup = (profile: CustomerProfileSummary) => {
    this.applyCustomerProfileSummary(profile);
    this.saveUserDetailsToStorage(
      profile.TeamOptIn ?? false,
      profile.SponsorOptIn ?? false,
      profile.ThirdOptIn ?? false,
    );
  };

  @action public restoreReturningFanSession = (
    profile?: CustomerProfileSummary,
    previousSession?: UniqueUser,
  ) => {
    this.applyCustomerProfileSummary(profile);

    if (!profile && previousSession) {
      this.selectedCustomerId = previousSession.customerProfileId || previousSession._id;
      this.selectedFirstName = previousSession.firstName || this.selectedFirstName;
      this.selectedLastName = previousSession.lastName || this.selectedLastName;
      this.selectedEmail = previousSession.email || this.selectedEmail;
      this.selectedPhone = previousSession.phoneNumber || this.selectedPhone;
      this.selectedZipCode = previousSession.zipCode || this.selectedZipCode;
      this.selectedBirthDate = previousSession.birthDate || this.selectedBirthDate;
      this.selectedCountry = previousSession.country || this.selectedCountry;
      this.selectedTeamOptIn = previousSession.TeamOptIn ?? this.selectedTeamOptIn;
      this.selectedSponsorOptIn = previousSession.SponsorOptIn ?? this.selectedSponsorOptIn;
      this.selectedThirdOptIn = previousSession.ThirdOptIn ?? this.selectedThirdOptIn;
      this.selectedIsSeasonTicketHolder = previousSession.isSeasonTicketHolder ?? this.selectedIsSeasonTicketHolder;
      this.saveUserDetailsToStorage(
        this.selectedTeamOptIn,
        this.selectedSponsorOptIn,
        this.selectedThirdOptIn,
      );
    }

    this.isReturningFanAuthenticated = true;
    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.RETURNING_FAN_AUTHENTICATED, "true");
    if (this.selectedCustomerId) {
      this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID, this.selectedCustomerId);
    }
    this.markReturningFanPromptHandled();

    if (!previousSession) {
      this.clearActiveRegistrationSession({ keepCustomerProfile: true });
      return;
    }

    this.selectedRegistrationUserId = previousSession._id
      ? String(previousSession._id)
      : null;
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_USER_ID,
      this.selectedRegistrationUserId || "",
    );
    this.bindRegistrationSessionKey(
      previousSession.game,
      previousSession.section,
      previousSession.row,
      previousSession.seat,
    );

    if (String(previousSession.game || "") === String(this.selectedGame || "")) {
      if (previousSession.section?.startsWith("Special: ")) {
        const sectionName = previousSession.section.substring(9);
        this.saveUsersPlace(true, sectionName, "", "", "", "");
      } else {
        const suite = getSuiteName(previousSession.section);
        if (suite) {
          this.saveUsersPlace(false, "", "", "", this.showSuites ? suite : "");
        } else {
          this.saveUsersPlace(
            false,
            previousSession.section,
            previousSession.row,
            previousSession.seat,
            "",
          );
        }
      }
    }
  }

  // not used: load last game
  @action public restorePreviousSession = async () => {
    if (!this.selectedEmail) {
      return;
    }

    const lastSession = await getLastSession(this.selectedEmail);
    if (!lastSession || !lastSession.game) {
      return;
    }

    await this.loadGameData(lastSession.game);
  }

  @action public saveUsersPlace = (
    isSpecialSection: boolean,
    section: string,
    row: string,
    seat: string,
    suite: string,
    sectionType?: string,
  ) => {
    const prevSpecial = this.selectedSectionIsSpecial;
    const prevSection = this.selectedSection;
    const prevRow = this.selectedRow;
    const prevSeat = this.selectedSeat;
    const prevSuite = this.selectedSuite;
    const prevGaName = this.selectedSpecialSection;

    clearStaleCameraForSeatContext();
    this.clearMomentCameraData();

    // GA slice requests use selectedPlace* for centering; those values come from the normal-seat
    // place map — if we keep them after reserved → GA, getSpecialSectionSlicedArea() skews badly.
    if (isSpecialSection && (!prevSpecial || prevGaName !== section)) {
      this.clearPersistedPlacePinForGa();
    }

    // Reserved-seat crop keys (global / moment) must not linger when moving to GA — getImageCropData
    // prefers global; leftover keys have been a source of mixed coordinate space after normal→GA.
    if (
      isSpecialSection
      && prevSpecial === false
      && this.selectedGame
      && prevSection
      && this.storageCropData
    ) {
      const reservedPrefix = `${this.selectedGame}_${prevSection}__`;
      let cropChanged = false;
      const next = { ...this.storageCropData };
      for (const k of Object.keys(next)) {
        if (k.startsWith(reservedPrefix)) {
          delete next[k];
          cropChanged = true;
        }
      }
      if (cropChanged) {
        this.storageCropData = next;
        this.saveDataToWorkerStorage(
          SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA,
          JSON.stringify(this.storageCropData),
        );
      }
    }

    const reservedSeatChanged = !isSpecialSection
      && prevSpecial === false
      && (
        prevSection !== section
        || prevRow !== row
        || prevSeat !== seat
        || prevSuite !== suite
      );
    if (reservedSeatChanged) {
      // Before writing new seat context, purge crop rows keyed to the previous reserved seat.
      // This prevents old global/moment crop data from carrying into another seat flow.
      this.removeImageCropData();
    }

    const hadPriorPlace = prevSpecial === true
      ? !!prevGaName
      : !!(prevSection || prevRow || prevSeat || prevSuite);
    const surnamePlaceChanged = isSpecialSection
      ? prevSpecial !== true || prevGaName !== section
      : prevSpecial === true
        || prevSection !== section
        || prevRow !== row
        || prevSeat !== seat
        || prevSuite !== suite;
    if (hadPriorPlace && surnamePlaceChanged) {
      this.clearVerifyLastName();
    }

    this.selectedSectionIsSpecial = isSpecialSection;

    if (this.selectedSectionIsSpecial) {
      this.selectedSpecialSection = section;
      this.selectedSectionType = sectionType;
      // Row/seat/suite are ignored for GA keys and APIs, but must not leak from a prior
      // reserved seat session — otherwise crop storage keys and UI state stay wrong.
      this.selectedRow = "";
      this.selectedSeat = "";
      this.selectedSuite = "";
      // Avoid stale numeric section (e.g. "101") from a prior reserved seat leaking into reads.
      this.selectedSection = "";
    } else {
      this.selectedSection = section;
      this.selectedRow = row;
      this.selectedSeat = seat;
      this.selectedSuite = suite;
    }

    if (this.selectedSectionIsSpecial) {
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPECIAL_SECTION, this.selectedSpecialSection,
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_TYPE, this.selectedSectionType,
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_IS_SPECIAL, "Yes",
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ROW, "",
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SEAT, "",
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SUITE, "",
      );
    } else {
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION, this.selectedSection,
      );

      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ROW, this.selectedRow,
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SEAT, this.selectedSeat,
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SUITE, this.selectedSuite,
      );
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_IS_SPECIAL, "No",
      );
    }

    // Entering GA after a normal seat (or first GA pick with null prev): wipe GA-adjacent stored
    // state for this game so behavior matches a fan who only ever chose GA — no reserved crop /
    // momento / place map carry-over.
    if (isSpecialSection && prevSpecial === false && this.selectedGame && section) {
      this.purgeCropDataForSpecialSection(this.selectedGame, section);
      this.purgeSelectedMomentosDataForGamePrefix(this.selectedGame);
      this.purgePlaceDataForGamePrefix(this.selectedGame);
      this.writeCenterGlobalCropForCurrentGaSection();
    }
  }

  /** Clear place-picker pin + worker PLACE_* so GA slicing is not centered on a prior normal-seat box. */
  @action private clearPersistedPlacePinForGa = (): void => {
    this.selectedPlaceX = null;
    this.selectedPlaceY = null;
    this.selectedPlaceWidth = null;
    this.selectedPlaceHeight = null;
    this.gaPlaceCenterSectionX = null;
    this.gaPlaceCenterSectionY = null;
    this.pendingGaPlaceCenterX = null;
    this.clearGaPersistedSlice();
    Promise.all([
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_X),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_Y),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_WIDTH),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_HEIGHT),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_X),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_Y),
    ]).catch(() => null);
  }

  public getGaPersistedSlice = (): {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null => {
    const {
      gaPersistedSliceX: x,
      gaPersistedSliceY: y,
      gaPersistedSliceW: width,
      gaPersistedSliceH: height,
    } = this;
    if (
      x == null
      || y == null
      || width == null
      || height == null
      || !Number.isFinite(x)
      || !Number.isFinite(y)
      || !Number.isFinite(width)
      || !Number.isFinite(height)
      || width <= 0
      || height <= 0
    ) {
      return null;
    }
    return { x, y, width, height };
  };

  @action public setGaPersistedSlice = (rect: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): void => {
    if (
      !Number.isFinite(rect.x)
      || !Number.isFinite(rect.y)
      || !Number.isFinite(rect.width)
      || !Number.isFinite(rect.height)
      || rect.width <= 0
      || rect.height <= 0
    ) {
      return;
    }
    this.gaPersistedSliceX = rect.x;
    this.gaPersistedSliceY = rect.y;
    this.gaPersistedSliceW = rect.width;
    this.gaPersistedSliceH = rect.height;
    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_X, `${rect.x}`);
    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_Y, `${rect.y}`);
    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_W, `${rect.width}`);
    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_H, `${rect.height}`);
  };

  @action private reconcileGaCenterFromPersistedSlice = (): void => {
    if (this.gaPlaceCenterSectionX != null && this.gaPlaceCenterSectionY != null) {
      return;
    }
    const slice = this.getGaPersistedSlice();
    const section = this.getSelectedSpecialSection();
    if (!slice || !section) {
      return;
    }
    const sectionX = section.sectionX || 0;
    const sectionY = section.sectionY || 0;
    this.saveGaPlaceCenter(
      slice.x + slice.width / 2 - sectionX,
      slice.y + slice.height / 2 - sectionY,
    );
  };

  @action public clearGaPersistedSlice = (): void => {
    this.gaPersistedSliceX = null;
    this.gaPersistedSliceY = null;
    this.gaPersistedSliceW = null;
    this.gaPersistedSliceH = null;
    Promise.all([
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_X),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_Y),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_W),
      this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_H),
    ]).catch(() => null);
  };

  /** Every imageCropData key for this game + GA section name (global, moment-, momento-scoped). */
  private purgeCropDataForSpecialSection = (gameId: string, specialSectionName: string): void => {
    if (!gameId || !specialSectionName || !this.storageCropData) return;

    const prefix = `${gameId}_${specialSectionName}_special_`;
    let changed = false;
    const next = { ...this.storageCropData };
    for (const k of Object.keys(next)) {
      if (k.startsWith(prefix)) {
        delete next[k];
        changed = true;
      }
    }
    if (changed) {
      this.storageCropData = next;
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA,
        JSON.stringify(this.storageCropData),
      );
    }
  }

  private purgeSelectedMomentosDataForGamePrefix = (gameId: string): void => {
    if (!gameId) return;
    if (!this.storageSelectedMomentosData) return;

    const prefix = `Selected_${gameId}_`;
    let changed = false;
    const next = { ...this.storageSelectedMomentosData };
    for (const k of Object.keys(next)) {
      if (k.startsWith(prefix)) {
        delete next[k];
        changed = true;
      }
    }
    if (changed) {
      this.storageSelectedMomentosData = next;
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTOS,
        JSON.stringify(this.storageSelectedMomentosData),
      );
    }
  }

  private purgePlaceDataForGamePrefix = (gameId: string): void => {
    if (!gameId) return;
    if (!this.storageSelectedPlaceData) return;

    const prefix = `Place_${gameId}_`;
    let changed = false;
    const next = { ...this.storageSelectedPlaceData };
    for (const k of Object.keys(next)) {
      if (k.startsWith(prefix)) {
        delete next[k];
        changed = true;
      }
    }
    if (changed) {
      this.storageSelectedPlaceData = next;
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_PLACE_DATA,
        JSON.stringify(this.storageSelectedPlaceData),
      );
    }
  }

  /**
   * Largest 8:10 rect in the GA slice, centered on the fan pin.
   * The slice is already centered on the place picker center.
   */
  public centeredFanViewRectInSlice = (
    sliced: { width: number; height: number; offsetX?: number; offsetY?: number },
  ): { x: number; y: number; width: number; height: number } | null => {
    const sliceW = sliced.width;
    const sliceH = sliced.height;
    if (!sliceW || !sliceH) return null;

    const offsetX = sliced.offsetX ?? 0;
    const offsetY = sliced.offsetY ?? 0;
    const fanCenterX = sliceW / 2 + offsetX;
    const fanCenterY = sliceH / 2 + offsetY;
    const aspect = ASPECT_RATIO_LIST["8_10"];

    let cropW: number;
    let cropH: number;
    if (sliceW / sliceH > aspect) {
      cropH = sliceH;
      cropW = cropH * aspect;
    } else {
      cropW = sliceW;
      cropH = cropW / aspect;
    }

    let cropX = fanCenterX - cropW / 2;
    let cropY = fanCenterY - cropH / 2;
    cropX = Math.max(0, Math.min(cropX, sliceW - cropW));
    cropY = Math.max(0, Math.min(cropY, sliceH - cropH));

    return {
      x: cropX, y: cropY, width: cropW, height: cropH,
    };
  };

  /**
   * Fan-view global crop: largest 8:10 rect in the GA slice, centered on the fan pin
   * (slice is API-centered on the place picker center).
   */
  @action public writeGaFanViewGlobalCropAtSliceFanCenter = (
    sliced: { width: number; height: number; offsetX?: number; offsetY?: number },
  ): void => {
    if (!this.hasCropStorageContext() || !this.isGaMomentContext()) return;
    const rect = this.centeredFanViewRectInSlice(sliced);
    if (!rect) return;

    this.saveImageCropData({
      ...rect,
      aspect: ASPECT_RATIO_LIST["8_10"],
    });
  };

  /**
   * Write the centered GA fanview only when the seat is still on the full slice
   * or a per-photo crop would hide the fanview.
   */
  @action public ensureCenteredFanViewCrop = (
    sliced: { width: number; height: number; offsetX?: number; offsetY?: number },
  ): void => {
    const rect = this.centeredFanViewRectInSlice(sliced);
    if (!rect || this.lockedFanViewAlreadyApplied(rect)) return;
    this.writeGaFanViewGlobalCropAtSliceFanCenter(sliced);
  };

  /** Repair legacy global crops saved as full square slice (1:1) after place save. */
  @action public repairGaFanViewGlobalCropIfNeeded = (
    sliced: { width: number; height: number; offsetX?: number; offsetY?: number },
  ): void => {
    if (!this.isGaMomentContext() || !this.hasCropStorageContext() || !this.storageCropData) return;
    const globalKey = this.generateCropDataKey();
    const global = this.storageCropData[globalKey] as ImageCropData | undefined;
    if (!global) return;

    const sliceW = sliced.width;
    const sliceH = sliced.height;
    const aspect = ASPECT_RATIO_LIST["8_10"];
    const isSquareFullSlice = global.width === global.height
      && sliceW > 0
      && Math.abs(global.width - sliceW) < 2;
    const missingFanAspect = global.aspect !== aspect;

    if (isSquareFullSlice || missingFanAspect) {
      this.writeGaFanViewGlobalCropAtSliceFanCenter(sliced);
    }
  };

  /** Centered 8:10 rectangle in slice space for current GA (overwrites global crop bucket). */
  private writeCenterGlobalCropForCurrentGaSection = (): void => {
    if (!this.canManipulateCropData || !this.selectedSectionIsSpecial) return;
    if (!this.selectedGame || !this.selectedSpecialSection) return;

    const special = this.specialSections.find((s) => s.name === this.selectedSpecialSection);
    const sliceW = special?.slicingWidth;
    const sliceH = special?.slicingHeight;
    if (!sliceW || !sliceH) return;

    const aspect = ASPECT_RATIO_LIST["8_10"];
    let cw: number;
    let ch: number;
    let cx: number;
    let cy: number;
    if (sliceW / sliceH > aspect) {
      ch = sliceH;
      cw = ch * aspect;
      cx = (sliceW - cw) / 2;
      cy = 0;
    } else {
      cw = sliceW;
      ch = cw / aspect;
      cx = 0;
      cy = (sliceH - ch) / 2;
    }

    this.saveImageCropData({
      x: cx,
      y: cy,
      width: cw,
      height: ch,
      aspect: ASPECT_RATIO_LIST["8_10"],
    });
  }

  /**
   * GA place picker: persist pin using section slicing size; only the center affects camera math.
   */
  /** GA only: read canonical section-space center (not cropper/wideshot pixels). */
  public getGaPlaceCenterInSectionSpace = (): { centerX: number; centerY: number } | null => {
    if (
      this.gaPlaceCenterSectionX != null
      && this.gaPlaceCenterSectionY != null
      && Number.isFinite(this.gaPlaceCenterSectionX)
      && Number.isFinite(this.gaPlaceCenterSectionY)
    ) {
      return {
        centerX: this.gaPlaceCenterSectionX,
        centerY: this.gaPlaceCenterSectionY,
      };
    }
    if (
      this.selectedPlaceX == null
      || this.selectedPlaceY == null
      || this.selectedPlaceWidth == null
      || this.selectedPlaceHeight == null
    ) {
      return null;
    }
    return {
      centerX: this.selectedPlaceX + this.selectedPlaceWidth / 2,
      centerY: this.selectedPlaceY + this.selectedPlaceHeight / 2,
    };
  };

  private syncGaPinRectFromCanonicalCenter = () => {
    if (
      this.gaPlaceCenterSectionX == null
      || this.gaPlaceCenterSectionY == null
      || !this.selectedSectionIsSpecial
    ) {
      return;
    }
    const section = this.getSelectedSpecialSection();
    if (!section) return;
    const pinW = section.slicingWidth || DEFAULT_SIZE;
    const pinH = section.slicingHeight || DEFAULT_SIZE;
    this.selectedPlaceX = this.gaPlaceCenterSectionX - pinW / 2;
    this.selectedPlaceY = this.gaPlaceCenterSectionY - pinH / 2;
    this.selectedPlaceWidth = pinW;
    this.selectedPlaceHeight = pinH;
  };

  @action public saveGaPlaceCenter = (centerX: number, centerY: number) => {
    if (!Number.isFinite(centerX) || !Number.isFinite(centerY)) {
      return;
    }
    this.gaPlaceCenterSectionX = centerX;
    this.gaPlaceCenterSectionY = centerY;
    const section = this.getSelectedSpecialSection();
    const pinW = section?.slicingWidth || DEFAULT_SIZE;
    const pinH = section?.slicingHeight || DEFAULT_SIZE;
    this.saveUsersPlaceRect(centerX - pinW / 2, centerY - pinH / 2, pinW, pinH);
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_X,
      `${centerX}`,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_Y,
      `${centerY}`,
    );
  };

  private applyGaPlaceCenterFromWorkerStorage = (centerY: number) => {
    if (!this.shouldApplyWorkerGaPlacement()) {
      this.pendingGaPlaceCenterX = null;
      return;
    }
    if (this.pendingGaPlaceCenterX == null || Number.isNaN(centerY)) {
      return;
    }
    this.saveGaPlaceCenter(this.pendingGaPlaceCenterX, centerY);
    this.pendingGaPlaceCenterX = null;
  };

  /** Fix draft/URL pins that stored crop-box pixel size instead of slicing dimensions. */
  @action public normalizeGaPlacePinIfNeeded = () => {
    if (!this.selectedSectionIsSpecial || !this.selectedSpecialSection) return;
    if (
      this.selectedPlaceX == null
      || this.selectedPlaceY == null
      || this.selectedPlaceWidth == null
      || this.selectedPlaceHeight == null
    ) {
      return;
    }
    const section = this.getSelectedSpecialSection();
    if (!section) return;

    const pinW = section.slicingWidth || DEFAULT_SIZE;
    const pinH = section.slicingHeight || DEFAULT_SIZE;
    const centerX = this.selectedPlaceX + this.selectedPlaceWidth / 2;
    const centerY = this.selectedPlaceY + this.selectedPlaceHeight / 2;

    if (this.selectedPlaceWidth < pinW || this.selectedPlaceHeight < pinH) {
      this.saveUsersPlaceRect(centerX - pinW / 2, centerY - pinH / 2, pinW, pinH);
    }
  };

  @action public saveUsersPlaceRect = (
    placeX: number,
    placeY: number,
    placeWidth: number,
    placeHeight: number,
  ) => {
    this.selectedPlaceX = placeX;
    this.selectedPlaceY = placeY;
    this.selectedPlaceWidth = placeWidth;
    this.selectedPlaceHeight = placeHeight;

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_X, `${this.selectedPlaceX}`,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_Y, `${this.selectedPlaceY}`,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_WIDTH, `${this.selectedPlaceWidth}`,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_HEIGHT, `${this.selectedPlaceHeight}`,
    );
  }

  @action public saveUserDetails = async (TeamOptIn: boolean, firstName: string,
    lastName: string, email: string, phone: string, zipCode: string, birthDate: string, country: string, SponsorOptIn: boolean, isSeasonTicketHolder?: boolean, ThirdOptIn?: boolean) => {
    this.selectedFirstName = firstName;
    this.selectedLastName = lastName;
    this.selectedEmail = email;
    this.selectedPhone = phone;
    this.selectedZipCode = zipCode;
    this.selectedBirthDate = birthDate;
    this.selectedCountry = country;
    this.selectedTeamOptIn = TeamOptIn;
    this.selectedSponsorOptIn = SponsorOptIn;
    if (typeof ThirdOptIn === "boolean") {
      this.selectedThirdOptIn = ThirdOptIn;
    }
    this.selectedIsSeasonTicketHolder = isSeasonTicketHolder ?? false;

    this.saveUserDetailsToStorage(TeamOptIn, SponsorOptIn, ThirdOptIn);
  };

  @action public updateSponsorOptIn = (value: boolean) => {
    this.selectedSponsorOptIn = value;
    this.saveUserDetailsToStorage(
      this.selectedTeamOptIn ?? false,
      value,
      this.selectedThirdOptIn ?? false,
    );
  };

  @action public updateTeamOptIn = (value: boolean) => {
    this.selectedTeamOptIn = value;
    this.saveUserDetailsToStorage(
      value,
      this.selectedSponsorOptIn ?? false,
      this.selectedThirdOptIn ?? false,
    );
  };

  @action public updateThirdOptIn = (value: boolean) => {
    this.selectedThirdOptIn = value;
    this.saveUserDetailsToStorage(
      this.selectedTeamOptIn ?? false,
      this.selectedSponsorOptIn ?? false,
      value,
    );
  };

  /** Persist that the watermark modal was shown (idempotent). */
  @action public markWatermarkOptInShown = async (): Promise<void> => {
    if (this.selectedWatermarkOptInShown === true) return;
    this.selectedWatermarkOptInShown = true;
    if (!this.selectedRegistrationUserId) return;
    try {
      await this.syncRegistrationSession({ WatermarkOptInShown: true });
    } catch (err) {
      console.error("Failed to persist WatermarkOptInShown:", err);
    }
  };

  /** Persist watermark modal acceptance (also marks shown). */
  @action public markWatermarkOptInAccepted = async (): Promise<void> => {
    this.selectedWatermarkOptInShown = true;
    this.selectedWatermarkOptIn = true;
  };

  private buildRegistrationSessionKey = (
    game?: string | null,
    section?: string | null,
    row?: string | null,
    seat?: string | null,
  ): string => (
    [String(game || ""), String(section || ""), String(row || ""), String(seat || "")].join("\u0001")
  );

  private parseRegistrationSessionKey = (key: string): {
    game: string;
    section: string;
    row: string;
    seat: string;
  } => {
    const [game = "", section = "", row = "", seat = ""] = String(key || "").split("\u0001");
    return { game, section, row, seat };
  };

  private isPlaceholderSeatLocation = (
    section?: string | null,
    row?: string | null,
    seat?: string | null,
  ): boolean => {
    const norm = (v?: string | null) => String(v || "").trim();
    const s = norm(section);
    const r = norm(row);
    const t = norm(seat);
    const emptyOrDash = (v: string) => !v || v === "-";
    return emptyOrDash(s) && emptyOrDash(r) && emptyOrDash(t);
  };

  @action private bindRegistrationSessionKey = (
    game?: string | null,
    section?: string | null,
    row?: string | null,
    seat?: string | null,
  ) => {
    this.registrationSessionKey = this.buildRegistrationSessionKey(game, section, row, seat);
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_SESSION_KEY,
      this.registrationSessionKey,
    );
  };

  /**
   * Drop the active User registration id so the next save creates a new session.
   * Keeps customerProfileId / contact fields so unique-user (email) identity is unchanged.
   */
  @action private clearActiveRegistrationSession = (
    options?: { keepCustomerProfile?: boolean },
  ) => {
    this.selectedRegistrationUserId = null;
    this.registrationSessionKey = null;
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_USER_ID,
      "",
    );
    this.deleteDataFromWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_SESSION_KEY,
    );
    if (!options?.keepCustomerProfile) {
      this.selectedCustomerId = null;
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID,
        "",
      );
    }
  };

  /**
   * True when game or seat changed vs the registration we already wrote — needs a new User row.
   * Completing lead-gen (first game and/or first real seat) updates the same registration.
   */
  private shouldCreateNewRegistrationSession = (
    game?: string | null,
    section?: string | null,
    row?: string | null,
    seat?: string | null,
  ): boolean => {
    if (!this.selectedRegistrationUserId) return false;
    if (!this.registrationSessionKey) return false;
    const nextKey = this.buildRegistrationSessionKey(game, section, row, seat);
    if (this.registrationSessionKey === nextKey) return false;

    const bound = this.parseRegistrationSessionKey(this.registrationSessionKey);
    const nextGame = String(game || "");
    const completingFirstGame = !bound.game && !!nextGame;
    const completingFirstSeat = this.isPlaceholderSeatLocation(
      bound.section, bound.row, bound.seat,
    ) && !this.isPlaceholderSeatLocation(section, row, seat);

    if (completingFirstGame || completingFirstSeat) {
      if (bound.game && nextGame && bound.game !== nextGame) return true;
      if (
        !this.isPlaceholderSeatLocation(bound.section, bound.row, bound.seat)
        && (
          String(bound.section) !== String(section || "")
          || String(bound.row) !== String(row || "")
          || String(bound.seat) !== String(seat || "")
        )
      ) {
        return true;
      }
      return false;
    }

    return true;
  };

  @action public syncRegistrationSession = async (
    patch: {
      game?: string;
      section?: string;
      row?: string;
      seat?: string;
      firstName?: string;
      lastName?: string;
      email?: string;
      phoneNumber?: string;
      zipCode?: string;
      birthDate?: string;
      country?: string;
      TeamOptIn?: boolean;
      SponsorOptIn?: boolean;
      WatermarkOptInShown?: boolean;
      WatermarkOptIn?: boolean;
      isSeasonTicketHolder?: boolean;
    } = {},
  ): Promise<void> => {
    if (!this.selectedRegistrationUserId) return;
    await updateRegistrationSessionRequest(this.selectedRegistrationUserId, {
      ...patch,
      updated: new Date(),
    });
  };

  @action public saveUserDetailsToDatabase = async (
    options?: { allowMissingGame?: boolean },
  ) => {
    const section = this.selectedSectionIsSpecial
      ? `Special: ${this.selectedSpecialSection}`
      : this.selectedSection;
    const row = this.selectedSectionIsSpecial
      ? `${this.selectedPlaceY}-${this.selectedPlaceHeight}`
      : `${this.selectedRow}`;
    const seat = this.selectedSectionIsSpecial
      ? `${this.selectedPlaceX}-${this.selectedPlaceWidth}`
      : `${this.selectedSeat}`;
    const hasSeatSelection = !!(section || this.selectedSectionIsSpecial);

    // save the user details to persistent storage
    const data = prepareSectionRowSeat(section, row, seat, this.selectedSuite,
      this.showSuites && !this.selectedSectionIsSpecial);
    const contactTeam = this.selectedGameData?.team1 ?? this.welcomeTeamContext;
    const profileEnabled = contactTeam?.profileEnabled === true;
    const allowMissingGame = options?.allowMissingGame === true || this.showUserDetailsFirst;
    try {
      // When creating an account, do Cognito sign-up FIRST so we don't create a profile
      // if the password fails policy or sign-up fails (only when profile is enabled)
      if (profileEnabled && this.pendingFanPassword && this.selectedEmail) {
        try {
          await cognitoSignUp(
            this.selectedEmail,
            this.pendingFanPassword,
            this.selectedFirstName || "",
            this.selectedLastName || "",
          );
          this.isReturningFanAuthenticated = true;
          this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.RETURNING_FAN_AUTHENTICATED, "true");
          this.clearPendingFanPassword();
        } catch (fanAuthError) {
          const errMsg = fanAuthError?.message || String(fanAuthError);
          if (errMsg.includes("SignUp is not permitted") || errMsg.includes("NotAuthorizedException")) {
            throw new Error("Account creation is not enabled for this app. Contact support to enable sign-in.");
          }
          if (errMsg.includes("InvalidPasswordException") || errMsg.includes("Password did not conform")) {
            throw new Error(errMsg.replace(/^.*?: /, "").trim() || "Password must be at least 8 characters and include numbers, uppercase, lowercase, and special characters.");
          }
          if (errMsg.includes("InvalidParameterException") || errMsg.includes("Invalid email")) {
            throw new Error("Please enter a valid email address.");
          }
          if (errMsg.includes("UsernameExistsException")) {
            throw new Error("An account with this email already exists. Try logging in instead.");
          }
          throw fanAuthError;
        }
      }

      // Server User model requires non-empty values for firstName, lastName, email, phoneNumber, zipCode, birthDate, country.
      // When operator has no fields to collect, use defaults and a unique random email per user.
      // When a team only collects some fields, use placeholders for the rest.
      const noFieldsToCollect = this.getHasNoUserDetailsFieldsToCollect();
      const teamName = contactTeam?.name?.toLowerCase() || "";
      const isSeahawks = teamName.includes("seahawks");
      const defaults = isSeahawks
        ? { lastName: "Seahawks User", email: "unknown@seahawks.com", phone: "1", zipCode: "98101", birthDate: "1", country: "United States" }
        : { lastName: "Guest", email: "unknown@mymomento.com", phone: "1", zipCode: "00000", birthDate: "1", country: "United States" };

      const uniqueEmail = noFieldsToCollect && !this.selectedEmail?.trim()
        ? `uniqueuser${Date.now().toString(36)}${Math.random().toString(36).substring(2, 10)}@mymomento.com`
        : null;

      // Only write opt-in booleans when we actually know them. `null ?? false` used to
      // overwrite a correct true (from User Details) with false after refresh/hydration,
      // because customerId/registrationId were restored from worker storage but opt-ins were not.
      const knownTeamOptIn = typeof this.selectedTeamOptIn === "boolean"
        ? this.selectedTeamOptIn
        : undefined;
      const knownSponsorOptIn = typeof this.selectedSponsorOptIn === "boolean"
        ? this.selectedSponsorOptIn
        : undefined;
      const knownThirdOptIn = typeof this.selectedThirdOptIn === "boolean"
        ? this.selectedThirdOptIn
        : undefined;

      const resolvedSection = hasSeatSelection ? data.section : "-";
      const resolvedRow = hasSeatSelection ? data.row : "-";
      const resolvedSeat = hasSeatSelection ? data.seat : "-";
      const sessionGame = this.selectedGame || "";

      const userPayload = {
        firstName: (this.selectedFirstName || "Unknown").trim() || "Unknown",
        lastName: (this.selectedLastName || defaults.lastName).trim() || defaults.lastName,
        email: (uniqueEmail ?? ((this.selectedEmail || defaults.email).trim() || defaults.email)),
        phoneNumber: (this.selectedPhone || defaults.phone).trim() || defaults.phone,
        zipCode: (this.selectedZipCode || defaults.zipCode).trim() || defaults.zipCode,
        birthDate: (this.selectedBirthDate || defaults.birthDate).trim() || defaults.birthDate,
        country: (this.selectedCountry || defaults.country).trim() || defaults.country,
        ...(knownTeamOptIn !== undefined ? { TeamOptIn: knownTeamOptIn } : {}),
        ...(knownSponsorOptIn !== undefined ? { SponsorOptIn: knownSponsorOptIn } : {}),
        ...(knownThirdOptIn !== undefined ? { ThirdOptIn: knownThirdOptIn } : {}),
        WatermarkOptInShown: this.selectedWatermarkOptInShown === true,
        WatermarkOptIn: this.selectedWatermarkOptIn === true,
        isSeasonTicketHolder: this.selectedIsSeasonTicketHolder,
        ...(this.selectedGame ? { game: this.selectedGame } : {}),
        section: resolvedSection,
        row: resolvedRow,
        seat: resolvedSeat,
      };

      // Same email switching game/seat → new User session (new registration id).
      // customerProfileId / unique email counts stay the same via profile match on email.
      if (
        this.shouldCreateNewRegistrationSession(
          sessionGame,
          resolvedSection,
          resolvedRow,
          resolvedSeat,
        )
      ) {
        this.clearActiveRegistrationSession({ keepCustomerProfile: true });
      }

      if (this.selectedRegistrationUserId) {
        await this.syncRegistrationSession(userPayload);
        this.bindRegistrationSessionKey(
          sessionGame,
          resolvedSection,
          resolvedRow,
          resolvedSeat,
        );
        return {
          _id: this.selectedRegistrationUserId,
          customerProfileId: this.selectedCustomerId,
        };
      }

      if (!this.selectedGame && !allowMissingGame) {
        throw new Error("Please select a game and try again.");
      }

      const response = await addUserRequest({
        ...userPayload,
        // New registrations still need explicit booleans for the User schema.
        TeamOptIn: knownTeamOptIn ?? false,
        SponsorOptIn: knownSponsorOptIn ?? false,
        ThirdOptIn: knownThirdOptIn ?? false,
      });
      this.selectedCustomerId = response?.customerProfileId || response?._id || this.selectedCustomerId;
      this.selectedRegistrationUserId = response?._id ? String(response._id) : null;
      this.bindRegistrationSessionKey(
        sessionGame,
        resolvedSection,
        resolvedRow,
        resolvedSeat,
      );
      if (this.selectedCustomerId) {
        this.saveDataToWorkerStorage(
          SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID,
          this.selectedCustomerId,
        );
      }
      if (this.selectedRegistrationUserId) {
        this.saveDataToWorkerStorage(
          SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_USER_ID,
          this.selectedRegistrationUserId,
        );
      }

      if (!profileEnabled && this.selectedEmail) {
        const hostnamePrefix = window.location.hostname.split(".")[0];
        try {
          localStorage.setItem(`momento_last_email_${hostnamePrefix}`, this.selectedEmail);
        } catch {
          // ignore
        }
      }

      return response;
    } catch (err) {
      console.log("Cannot save user details", err);
      throw err;
    }
  }

  private saveEmailToStorage = async (TeamOptIn: boolean) => {
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.EMAIL, TeamOptIn ? "" : this.selectedEmail,
    );
  }

  private saveUserDetailsToStorage = async (
    TeamOptIn: boolean,
    SponsorOptIn: boolean,
    ThirdOptIn?: boolean,
  ) => {
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID,
      this.selectedCustomerId || "",
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_USER_ID,
      this.selectedRegistrationUserId || "",
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.FIRST_NAME,
      this.selectedFirstName,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.LAST_NAME,
      this.selectedLastName,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.EMAIL,
      this.selectedEmail,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PHONE,
      this.selectedPhone,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ZIP_CODE,
      this.selectedZipCode,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.BIRTHDATE,
      this.selectedBirthDate,
    );
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.COUNTRY,
      this.selectedCountry,
    );
    if (typeof TeamOptIn === "boolean") {
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.TEAM_OPT_IN,
        TeamOptIn ? "Yes" : "No",
      );
    }
    if (typeof SponsorOptIn === "boolean") {
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPONSOR_OPT_IN,
        SponsorOptIn ? "Yes" : "No",
      );
    }
    if (typeof ThirdOptIn === "boolean") {
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.THIRD_OPT_IN,
        ThirdOptIn ? "Yes" : "No",
      );
    }
  };

  @action saveShowMomentCaption = (show: boolean) => {
    this.selectedShowMomentCaption = show;

    const gameId = this.selectedGameData?._id?.toString();
    if (gameId) {
      const next = { ...this.showMomentCaptionByGame, [gameId]: show };
      this.showMomentCaptionByGame = next;
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION_BY_GAME,
        JSON.stringify(next),
      );
    }

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION,
      show ? "Yes" : "No",
    );
  }

  // TODO: change to actual type
  @action saveMoment = (momentId: string, momentoSlide?: number) => {
    this.selectedMoment = momentId;

    this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.MOMENT, momentId);

    if (momentoSlide !== undefined) {
      this.selectedMomento = momentoSlide || 0;
      this.saveDataToWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTO, `${momentoSlide}`);
    }
  }

  @action deleteCurrentMoment = () => {
    this.selectedMoment = null;
    this.selectedMomento = null;
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.MOMENT);
    this.deleteDataFromWorkerStorage(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTO);
  }

  @action public saveImageCropData = (
    cropData: ImageCropData,
    momentId?: string,
    momentoIndex?: number,
  ) => {
    // Writes must not depend on `canManipulateCropData()` (it can spuriously fail during GA
    // hydrate when `selectedSectionIsSpecial` is still null). Same seat/game gate as reads.
    if (!this.hasCropStorageContext()) return;

    const matchKey = this.generateCropDataKey(momentId, momentoIndex);
    const matchPrefix = matchKey ? `${matchKey}_` : "";

    if (this.storageCropData) {
      for (const key of Object.keys(this.storageCropData)) {
        if (key === matchKey || (matchPrefix && key.startsWith(matchPrefix))) {
          delete this.storageCropData[key];
        }
      }
    }

    this.storageCropData = {
      ...this.storageCropData,
      [matchKey]: cropData,
    };

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA,
      JSON.stringify(this.storageCropData),
    );
  }

  /**
   * Removes only the global crop data key (filter/custom adjustments).
   * Does not affect momento- or moment-specific crop data.
   */
  @action public removeGlobalCropData = (): void => {
    if (!this.hasCropStorageContext() || !this.storageCropData) return;

    const globalKey = this.generateCropDataKey();
    if (this.storageCropData[globalKey]) {
      delete this.storageCropData[globalKey];
      this.storageCropData = { ...this.storageCropData };
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA,
        JSON.stringify(this.storageCropData),
      );
    }
  }

  /**
   * Saves filter and custom adjustments to the global key so they apply to all photos.
   * Merges with existing global data to preserve crop fallbacks.
   */
  @action public saveGlobalFilterAndAdjustments = (
    filter: ImageCropData["filter"],
    customAdjustments: ImageCropData["customAdjustments"],
  ): void => {
    if (!this.hasCropStorageContext()) return;

    const globalKey = this.generateCropDataKey();
    const existing = (this.storageCropData?.[globalKey] || {}) as Partial<ImageCropData>;
    const merged: ImageCropData = {
      x: existing.x ?? 0,
      y: existing.y ?? 0,
      width: existing.width ?? 400,
      height: existing.height ?? 400,
      ...existing,
      filter,
      customAdjustments,
    };
    this.storageCropData = {
      ...this.storageCropData,
      [globalKey]: merged,
    };

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA,
      JSON.stringify(this.storageCropData),
    );
  }

  public getImageCropData = (momentId: string, momentoIndex: number): ImageCropData|null => {
    // Read must not call canManipulateCropData() (toasts) — returning null breaks fan crop after
    // navigations where validation would spuriously fail while seat/game context is still fine.
    if (!this.storageCropData || !this.hasCropStorageContext()) return null;

    const momentoKey = this.generateCropDataKey(momentId, momentoIndex);
    const momentKey = this.generateCropDataKey(momentId);
    const globalKey = this.generateCropDataKey();

    const globalData = this.storageCropData[globalKey] as ImageCropData | undefined;
    const momentoData = this.storageCropData[momentoKey] as ImageCropData | undefined;
    const momentData = this.storageCropData[momentKey] as ImageCropData | undefined;

    // Most specific wins: per-momento / per-moment saves must not be masked by a seeded or
    // "apply to all" global bucket (e.g. GA center crop after reserved→GA).
    return momentoData || momentData || globalData || null;
  }

  /** True only if a per-momento row exists (not moment-only or global fallback). */
  public hasMomentoSpecificCropData = (momentId: string, momentoIndex: number): boolean => {
    if (!this.storageCropData || !this.hasCropStorageContext()) {
      return false;
    }
    const momentoKey = this.generateCropDataKey(momentId, momentoIndex);
    return Object.prototype.hasOwnProperty.call(this.storageCropData, momentoKey)
      && this.storageCropData[momentoKey] != null;
  };

  /**
   * True if the seat-global crop fallback row already exists. Used by the My Moments
   * seat-level seeder to avoid clobbering a previously-good seed every time the
   * `momentList` reference changes (page-2 fetch, View All return, MobX re-derive).
   */
  public hasGlobalCropData = (): boolean => {
    if (!this.storageCropData || !this.hasCropStorageContext()) {
      return false;
    }
    const globalKey = this.generateCropDataKey();
    return Object.prototype.hasOwnProperty.call(this.storageCropData, globalKey)
      && this.storageCropData[globalKey] != null;
  };

  /** Per-moment or per-photo rows win over the seat-global fanview in getImageCropData. */
  public hasCropMoreSpecificThanGlobal = (): boolean => {
    if (!this.storageCropData || !this.hasCropStorageContext()) return false;
    const prefix = `${this.generateCropDataKey()}_`;
    return Object.keys(this.storageCropData).some(
      (key) => key.startsWith(prefix) && this.storageCropData[key] != null,
    );
  };

  /** True when the seat-global crop is already this fanview and nothing more specific masks it. */
  public lockedFanViewAlreadyApplied = (
    rect: { x: number; y: number; width: number; height: number },
  ): boolean => {
    if (this.hasCropMoreSpecificThanGlobal()) return false;
    if (!this.storageCropData || !this.hasCropStorageContext()) return false;
    const global = this.storageCropData[this.generateCropDataKey()] as ImageCropData | undefined;
    if (!global) return false;
    const close = (a: number, b: number) => Math.abs((a || 0) - (b || 0)) < 1.5;
    return close(global.x, rect.x)
      && close(global.y, rect.y)
      && close(global.width, rect.width)
      && close(global.height, rect.height);
  };

  public removeImageCropData = (
    momentId?: string,
    momentoIndex?: number,
  ): void => {
    const matchKey = this.generateCropDataKey(momentId, momentoIndex);

    if (!this.storageCropData || Object.keys(this.storageCropData).length === 0) {
      return;
    }

    for (const key of Object.keys(this.storageCropData)) {
      if (key.match(matchKey)) {
        delete this.storageCropData[key];
      }
    }

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA,
      JSON.stringify(this.storageCropData),
    );
  }

  public generateCropDataKey = (momentId?: string, momentoIndex? : number): string => {
    const ga = this.isGaMomentContext();
    const specialSection = ga ? "special" : "";
    const selectedSection = ga ? this.selectedSpecialSection : this.selectedSection;
    const basicKey = `${this.selectedGame}_${selectedSection}_${specialSection}_${this.selectedRow}_${this.selectedSeat}_${this.selectedSuite}`;
    let resultKey = basicKey;
    if (momentId) {
      resultKey = `${resultKey}_${momentId}`;
    }
    if (momentoIndex || momentoIndex === 0) {
      resultKey = `${resultKey}_${momentoIndex}`;
    }
    return resultKey;
  }

  /** True when the fan chose a suite instead of section/row/seat (seat fields stay empty). */
  private hasSuiteSeatContext = (): boolean => {
    return !!(this.showSuites && this.selectedSuite?.trim());
  };

  /** Same seat/game rules as saves, but silent — used for display-time crop lookup. */
  private hasCropStorageContext = (): boolean => {
    if (!this.selectedGame) return false;
    if (this.selectedSectionIsSpecial === true) return true;
    // GA restored from worker before SECTION_IS_SPECIAL hydrates: pins + Vision name already present.
    const hydrateGaPins = this.selectedSectionIsSpecial == null
      && !!this.selectedSpecialSection?.trim()
      && this.selectedPlaceX != null
      && this.selectedPlaceY != null
      && this.selectedPlaceWidth != null
      && this.selectedPlaceHeight != null;
    if (hydrateGaPins) return true;
    // Suite flow clears section/row/seat; treat selectedSuite like a reserved seat for crop I/O.
    if (
      !this.selectedSeat
      && !this.hasSuiteSeatContext()
      && (this.selectedSectionIsSpecial === false || this.selectedSectionIsSpecial == null)
    ) {
      return false;
    }
    return true;
  };

  /**
   * GA / special flows use empty row+seat; `selectedSectionIsSpecial` can briefly be null on refresh
   * while PLACE_* and SPECIAL_SECTION hydrate from the service worker.
   */
  public isGaMomentContext = (): boolean => {
    if (this.selectedSectionIsSpecial === true) return true;
    return (
      this.selectedSectionIsSpecial !== false
      && !!this.selectedSpecialSection?.trim()
      && this.selectedPlaceX != null
      && this.selectedPlaceY != null
      && this.selectedPlaceWidth != null
      && this.selectedPlaceHeight != null
    );
  };

  public hasReservedSeatForGameMoments = (): boolean => {
    if (this.hasSuiteSeatContext()) {
      return true;
    }
    return !!(this.selectedSection && this.selectedRow && this.selectedSeat);
  };

  /**
   * Worker GAME / SECTION_IS_SPECIAL can resolve before SPECIAL_SECTION + PLACE_* on refresh.
   * Building getGameMoments before then sends an empty `sectionName` and the API returns
   * "Camera with such place not found".
   */
  public isMomentFetchSeatContextReady = (): boolean => {
    if (!this.selectedGame) return false;
    if (this.isGaMomentContext()) {
      return !!this.selectedSpecialSection?.trim()
        && (!!this.getGaPlaceCenterInSectionSpace() || !!this.getGaPersistedSlice());
    }
    if (this.hasReservedSeatForGameMoments()) return true;
    return false;
  };

  /** Silent read guard for place-picker JSON (must not use `canManipulateCropData` — it toasts during hydrate). */
  private hasPlaceReadContext = (): boolean => {
    if (!this.selectedGame || !this.storageSelectedPlaceData) return false;
    if (this.selectedSectionIsSpecial === true && this.selectedSpecialSection?.trim()) return true;
    if (
      this.selectedSectionIsSpecial !== false
      && this.selectedSpecialSection?.trim()
      && this.selectedPlaceX != null
      && this.selectedPlaceY != null
      && this.selectedPlaceWidth != null
      && this.selectedPlaceHeight != null
    ) {
      return true;
    }
    if (this.selectedSeat || this.hasSuiteSeatContext()) return true;
    return false;
  };

  private canManipulateCropData = () => {
    let valid = true;
    if (!this.selectedGame) {
      showErrorNotification("No game selected");
      valid = false;
    }
    if (
      !this.selectedSeat
      && !this.hasSuiteSeatContext()
      && !this.selectedSectionIsSpecial
      && !this.isGaMomentContext()
    ) {
      showErrorNotification("No seat selected");
      valid = false;
    }
    return valid;
  }

  @action public saveSelectedMomentosData = (
    data: SelectedMomentosData,
    momentId?: string,
  ) => {
    if (!this.canManipulateCropData) return;

    const key = this.generateSelectedMomentosDataKey(momentId);

    this.storageSelectedMomentosData = {
      ...this.storageSelectedMomentosData,
      [key]: data,
    };

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTOS,
      JSON.stringify(this.storageSelectedMomentosData),
    );
  }

  public getSelectedMomentosData = (momentId: string): SelectedMomentosData | null => {
    if (!this.hasCropStorageContext() || !this.storageSelectedMomentosData) return {};

    const momentKey = this.generateSelectedMomentosDataKey(momentId);
    if (this.storageSelectedMomentosData[momentKey]) {
      return this.storageSelectedMomentosData[momentKey] || {};
    }

    const globalKey = this.generateSelectedMomentosDataKey();
    return this.storageSelectedMomentosData[globalKey] || {};
  }

  public removeSelectedMomentosData = (
    momentId?: string,
  ): void => {
    const matchKey = this.generateSelectedMomentosDataKey(momentId);

    if (!this.storageSelectedMomentosData
      || Object.keys(this.storageSelectedMomentosData).length === 0) {
      message.info("There's nothing to remove");
      return;
    }

    for (const key of Object.keys(this.storageSelectedMomentosData)) {
      if (key.match(matchKey)) {
        delete this.storageSelectedMomentosData[key];
      }
    }

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTOS,
      JSON.stringify(this.storageSelectedMomentosData),
    );
  }

  public generateSelectedMomentosDataKey = (momentId?: string): string => {
    let basicKey: string;
    if (this.isGaMomentContext()) {
      basicKey = `Selected_${this.selectedGame}_${this.selectedSpecialSection}_${this.selectedPlaceX}_${this.selectedPlaceY}`;
    } else if (this.hasSuiteSeatContext()) {
      // Suite saves leave section/row/seat empty — key by suite so crops/picks do not collide.
      basicKey = `Selected_${this.selectedGame}____${this.selectedSuite}`;
    } else {
      basicKey = `Selected_${this.selectedGame}_${this.selectedSection}_${this.selectedRow}_${this.selectedSeat}`;
    }
    let resultKey = basicKey;
    if (momentId) {
      resultKey = `${resultKey}_${momentId}`;
    }
    return resultKey;
  }

  public getSelectedSpecialSection = () => {
    if (this.selectedSectionIsSpecial === false || !this.selectedSpecialSection?.trim()) {
      return null;
    }

    const specialSection = this.specialSections.find((sect) => sect.name === this.selectedSpecialSection);
    if (specialSection) {
      return specialSection;
    }
    if (this.specialSectionsListLoading) {
      return null;
    }
    return null;
  }

  private canManipulatePlaceData = () => {
    let valid = true;
    if (!this.selectedGame) {
      showErrorNotification("No game selected");
      valid = false;
    }

    if (!this.getSelectedSpecialSection()) {
      showErrorNotification("No special section selected");
      valid = false;
    }

    return valid;
  }

  @action public saveSelectedPlaceData = (
    cropData: ImageCropData,
  ) => {
    if (!this.canManipulatePlaceData) return;

    const matchKey = this.generatePlaceCropDataKey();

    // clear all previous data
    if (this.storageSelectedPlaceData) {
      for (const key of Object.keys(this.storageSelectedPlaceData)) {
        if (key.match(matchKey)) {
          delete this.storageSelectedPlaceData[key];
        }
      }
    }

    this.storageSelectedPlaceData = {
      ...this.storageSelectedPlaceData,
      [matchKey]: cropData,
    };

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_PLACE_DATA,
      JSON.stringify(this.storageSelectedPlaceData),
    );
  }

  public getSpecialSectionSlicingSize = (): number => {
    const specialSection = this.getSelectedSpecialSection();
    const width = specialSection?.slicingWidth || DEFAULT_SIZE;
    const height = specialSection?.slicingHeight || DEFAULT_SIZE;
    const size = width > height ? height : width;
    return size;
  }

  public getDefaultPlaceData = (): ImageCropData => {
    const size = this.getSpecialSectionSlicingSize();

    return {
      x: size > 50 ? 20 : 0,
      y: size > 50 ? 20 : 0,
      width: size > 50 ? size - 40 : size,
      height: size > 50 ? size - 40 : size,
      aspect: 1.0,
    };
  }

  public getSelectedPlaceData = (): ImageCropData | null => {
    if (!this.hasPlaceReadContext()) {
      return this.getDefaultPlaceData();
    }

    const globalKey = this.generatePlaceCropDataKey();
    return this.storageSelectedPlaceData[globalKey] || this.getDefaultPlaceData();
  }

  public removeSelectedPlaceData = (): void => {
    const matchKey = this.generatePlaceCropDataKey();

    if (!this.storageSelectedPlaceData || Object.keys(this.storageSelectedPlaceData).length === 0) {
      message.info("There's nothing to remove");
      return;
    }

    for (const key of Object.keys(this.storageSelectedPlaceData)) {
      if (key.match(matchKey)) {
        delete this.storageSelectedPlaceData[key];
      }
    }

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_PLACE_DATA,
      JSON.stringify(this.storageSelectedPlaceData),
    );
  }

  public generatePlaceCropDataKey = (): string => {
    const basicKey = `Place_${this.selectedGame}_${this.selectedSpecialSection}`;
    return basicKey;
  }

  @action public saveShownTips = (tipsCategory: string, show: boolean) => {
    this.shownTipsData = {
      ...this.shownTipsData,
      [tipsCategory]: show,
    };

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOWN_TIPS, JSON.stringify(this.shownTipsData),
    );
  }

  @action public saveLastPlaces = (last: LastPlace[]) => {
    this.lastPlaces = [
      ...last,
    ];

    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.LAST_PLACES, JSON.stringify(this.lastPlaces),
    );
  }

  private fetchDataFromWorkerStorage = (entity: SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM): Promise<void> => {
    const existing = this.workerFetchPromises.get(entity);
    if (existing) return existing;

    const promise = this.readDataFromWorkerStorage(entity).finally(() => {
      this.workerFetchPromises.delete(entity);
    });
    this.workerFetchPromises.set(entity, promise);
    return promise;
  };

  private readDataFromWorkerStorage = async (entity: SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM) => {
    try {
      let { data } = await axios.get<string>(entity);
      // sometimes axios return main html instead of the new key. Clear such values
      // eslint-disable-next-line quotes
      if (data && data.startsWith('<!doctype html><html lang="en"><head><meta charset="utf-8"/><link rel="icon" href="./favicon.ico"/>')) {
        data = "";
      }

      if (data) {
        runInAction(() => {
          switch (entity) {
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GAME:
              console.log(`> Found stored game ${data}`);
              if (this.selectGameInProgress) {
                break;
              }
              if (!this.selectedGame) {
                this.selectedGame = data;
                this.bootstrapGameDataAfterWorkerHydration();
              } else if (String(this.selectedGame) !== String(data)) {
                console.warn(
                  `[worker GAME] Ignoring stale stored game ${data} (current selection ${this.selectedGame})`,
                );
              } else if (!this.gameDataMatchesSelection()) {
                this.bootstrapGameDataAfterWorkerHydration();
              }
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION:
              console.log(`> Found stored section ${data}`);
              this.selectedSection = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ROW:
              console.log(`> Found stored row ${data}`);
              this.selectedRow = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SEAT:
              console.log(`> Found stored seat ${data}`);
              this.selectedSeat = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SUITE:
              console.log(`> Found stored suite ${data}`);
              this.selectedSuite = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.MOMENT:
              console.log(`> Found stored moment ${data}`);
              this.selectedMoment = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTO:
              console.log(`> Found stored momento ${data}`);
              this.selectedMomento = Number(data);
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA:
              console.log(`> Found stored crop data ${data}`);
              this.storageCropData = JSON.parse(data);
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_PLACE_DATA:
              console.log(`> Found stored place data ${data}`);
              this.storageSelectedPlaceData = JSON.parse(data);
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SELECTED_MOMENTOS:
              console.log(`> Found stored selection data ${data}`);
              this.storageSelectedMomentosData = JSON.parse(data);
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOWN_TIPS:
              console.log(`> Found shown tips data ${data}`);
              this.shownTipsData = JSON.parse(data);
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.LAST_PLACES:
              console.log(`> Found last places data ${data}`);
              this.lastPlaces = data ? JSON.parse(data) : [];
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.VERIFY_LAST_NAME:
              console.log(`> Found stored verify last name data ${data}`);
              this.selectedVerifyLastName = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.FIRST_NAME:
              console.log(`> Found stored firstName data ${data}`);
              this.selectedFirstName = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.LAST_NAME:
              console.log(`> Found stored lastName data ${data}`);
              this.selectedLastName = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.EMAIL:
              console.log(`> Found stored email data ${data}`);
              this.selectedEmail = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CUSTOMER_ID:
              console.log(`> Found stored customer id ${data}`);
              this.selectedCustomerId = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_USER_ID:
              console.log(`> Found stored registration user id ${data}`);
              this.selectedRegistrationUserId = data || null;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.REGISTRATION_SESSION_KEY:
              console.log("> Found stored registration session key");
              this.registrationSessionKey = data || null;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PHONE:
              console.log(`> Found stored phone data ${data}`);
              this.selectedPhone = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ZIP_CODE:
              console.log(`> Found stored zip code data ${data}`);
              this.selectedZipCode = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.BIRTHDATE:
              console.log(`> Found stored birth date data ${data}`);
              this.selectedBirthDate = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.COUNTRY:
              console.log(`> Found stored country data ${data}`);
              this.selectedCountry = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.TEAM_OPT_IN:
              console.log(`> Found stored team opt-in ${data}`);
              this.selectedTeamOptIn = data === "Yes";
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SPONSOR_OPT_IN:
              console.log(`> Found stored sponsor opt-in ${data}`);
              this.selectedSponsorOptIn = data === "Yes";
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.THIRD_OPT_IN:
              console.log(`> Found stored third opt-in ${data}`);
              this.selectedThirdOptIn = data === "Yes";
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION:
              console.log(`> Found stored show moment caption ${data}`);
              this.legacySingleShowMomentCaptionHydrated = true;
              this.legacySingleShowMomentCaptionValue = data === "Yes";
              this.selectedShowMomentCaption = data === "Yes";
              this.syncShowMomentCaptionPreferenceForLoadedGame();
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION_BY_GAME: {
              console.log(`> Found stored show moment caption map ${data}`);
              try {
                const parsed = data ? JSON.parse(data) : {};
                const next: Record<string, boolean> = {};
                if (parsed && typeof parsed === "object") {
                  for (const [k, v] of Object.entries(parsed)) {
                    next[String(k)] = v === true || v === "Yes";
                  }
                }
                this.showMomentCaptionByGame = next;
              } catch {
                this.showMomentCaptionByGame = {};
              }
              this.syncShowMomentCaptionPreferenceForLoadedGame();
              break;
            }
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.RETURNING_FAN_AUTHENTICATED:
              this.isReturningFanAuthenticated = data === "true";
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_IS_SPECIAL:
              console.log(`> Found stored section is special flag ${data}`);
              this.selectedSectionIsSpecial = data === "Yes";
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SECTION_TYPE:
              console.log(`> Found stored section type ${data}`);
              this.selectedSectionType = data;
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_X:
              console.log(`> Found stored Place X ${data}`);
              if (this.shouldApplyWorkerGaPlacement()) {
                if (
                  this.gaPlaceCenterSectionX == null
                  || this.gaPlaceCenterSectionY == null
                ) {
                  this.selectedPlaceX = Number(data);
                }
                this.syncGaPinRectFromCanonicalCenter();
              }
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_Y:
              console.log(`> Found stored Place Y ${data}`);
              if (this.shouldApplyWorkerGaPlacement()) {
                if (
                  this.gaPlaceCenterSectionX == null
                  || this.gaPlaceCenterSectionY == null
                ) {
                  this.selectedPlaceY = Number(data);
                }
                this.syncGaPinRectFromCanonicalCenter();
              }
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_WIDTH:
              console.log(`> Found stored Place Width ${data}`);
              if (this.shouldApplyWorkerGaPlacement()) {
                if (
                  this.gaPlaceCenterSectionX == null
                  || this.gaPlaceCenterSectionY == null
                ) {
                  this.selectedPlaceWidth = Number(data);
                }
                this.syncGaPinRectFromCanonicalCenter();
              }
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_HEIGHT:
              console.log(`> Found stored Place Height ${data}`);
              if (this.shouldApplyWorkerGaPlacement()) {
                if (
                  this.gaPlaceCenterSectionX == null
                  || this.gaPlaceCenterSectionY == null
                ) {
                  this.selectedPlaceHeight = Number(data);
                }
                this.syncGaPinRectFromCanonicalCenter();
              }
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_X: {
              if (!this.shouldApplyWorkerGaPlacement()) {
                this.pendingGaPlaceCenterX = null;
                break;
              }
              const cx = Number(data);
              if (!Number.isNaN(cx)) {
                this.pendingGaPlaceCenterX = cx;
              }
              break;
            }
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.PLACE_CENTER_Y: {
              if (!this.shouldApplyWorkerGaPlacement()) {
                this.pendingGaPlaceCenterX = null;
                break;
              }
              const cy = Number(data);
              if (!Number.isNaN(cy)) {
                this.applyGaPlaceCenterFromWorkerStorage(cy);
              }
              break;
            }
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_X: {
              if (!this.shouldApplyWorkerGaPlacement()) break;
              const v = Number(data);
              if (!Number.isNaN(v)) this.gaPersistedSliceX = v;
              break;
            }
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_Y: {
              if (!this.shouldApplyWorkerGaPlacement()) break;
              const v = Number(data);
              if (!Number.isNaN(v)) this.gaPersistedSliceY = v;
              break;
            }
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_W: {
              if (!this.shouldApplyWorkerGaPlacement()) break;
              const v = Number(data);
              if (!Number.isNaN(v)) this.gaPersistedSliceW = v;
              break;
            }
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.GA_SLICE_H: {
              if (!this.shouldApplyWorkerGaPlacement()) break;
              const v = Number(data);
              if (!Number.isNaN(v)) {
                this.gaPersistedSliceH = v;
                this.reconcileGaCenterFromPersistedSlice();
              }
              break;
            }
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ENHANCED_GA_SESSION:
              if (data === "true") {
                this.enhancedGaSessionActive = true;
              }
              break;
            case SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.ENHANCED_GA_SUB: {
              const idx = Number(data);
              if (!Number.isNaN(idx)) {
                this.enhancedGaSubsectionIndex = idx;
              }
              break;
            }
            default:
              break;
          }
        });
      } else {
        console.log(`> No data was found in the storage for entity ${entity}`);
      }
    } catch (err) {
      console.log("Something went wrong while searching for the data in the worker's storage");
    }
  }

  private syncShowMomentCaptionPreferenceForLoadedGame = () => {
    runInAction(() => {
      const game = this.selectedGameData;
      const gameId = game?._id?.toString();
      const home = game?.team1;
      if (!gameId || !home || !homeTeamAllowsFanScoreboard(game)) {
        return;
      }
      const map = { ...this.showMomentCaptionByGame };
      if (map[gameId] === undefined) {
        if (Object.keys(map).length === 0 && this.legacySingleShowMomentCaptionHydrated) {
          map[gameId] = this.legacySingleShowMomentCaptionValue;
        } else {
          map[gameId] = home.momentScoreboardDefaultOnPhoto !== false;
        }
        this.showMomentCaptionByGame = map;
        this.saveDataToWorkerStorage(
          SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION_BY_GAME,
          JSON.stringify(map),
        );
      }
      const next = this.showMomentCaptionByGame[gameId];
      this.selectedShowMomentCaption = next;
      this.saveDataToWorkerStorage(
        SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.SHOW_MOMENT_CAPTION,
        next ? "Yes" : "No",
      );
    });
  };

  private fetchGameData = async (
    gameId?: string,
    mode: FetchGameDataMode = "full",
  ): Promise<boolean> => {
    const idToFetch = (gameId || this.selectedGame)?.toString();
    if (!idToFetch) {
      console.warn("fetchGameData skipped: no game ID available");
      return false;
    }

    const hasMatchingGame = this.gameDataMatchesSelection(idToFetch);
    // Core can reuse any matching payload (lite or full). Full must upgrade fanLite.
    const needsGameFetch = !hasMatchingGame
      || (mode === "full" && this.gameDataIsFanLite);

    if (needsGameFetch) {
      const useFanLite = mode === "core";
      let corePromise = this.fetchGameDataPromises.get(`${idToFetch}:${useFanLite ? "lite" : "full"}`);
      if (!corePromise) {
        corePromise = this.runFetchGameDataCore(idToFetch, gameId, useFanLite);
        this.fetchGameDataPromises.set(`${idToFetch}:${useFanLite ? "lite" : "full"}`, corePromise);
        corePromise.finally(() => {
          if (this.fetchGameDataPromises.get(`${idToFetch}:${useFanLite ? "lite" : "full"}`) === corePromise) {
            this.fetchGameDataPromises.delete(`${idToFetch}:${useFanLite ? "lite" : "full"}`);
          }
        }).catch(() => undefined);
      }
      const coreOk = await corePromise;
      if (!coreOk) {
        return false;
      }
    }

    if (mode === "full") {
      const selectionStillCurrent = () => idToFetch === (this.selectedGame || gameId)?.toString();
      if (!selectionStillCurrent()) {
        return true;
      }
      await this.fetchSpecialSectionsData();
      if (!selectionStillCurrent()) {
        return true;
      }
      await this.ensureHeaderBrandAssets();
      if (selectionStillCurrent() && !isMobileDevice()) {
        this.scheduleTeamVisualAssets(idToFetch);
      }
    }

    return true;
  };

  private runFetchGameDataCore = async (
    idToFetch: string,
    gameId: string | undefined,
    fanLite: boolean,
  ): Promise<boolean> => {
    const selectionStillCurrent = () => idToFetch === (this.selectedGame || gameId)?.toString();

    try {
      const gameData = await getGameDataByIdRequest(idToFetch, { fanLite });
      if (!selectionStillCurrent()) {
        return false;
      }

      runInAction(() => {
        this.selectedGameData = gameData;
        this.gameDataIsFanLite = fanLite;
        this.syncShowMomentCaptionPreferenceForLoadedGame();
        this.hidePurchaseActions = this.selectedGameData?.team1?.hidePurchase
          || this.selectedGameData?.team2?.hidePurchase;
        this.purchasePopupAfterDownloadedImage = !!this.selectedGameData?.team1
          ?.purchasePopupAfterDownloadedImage && !this.hidePurchaseActions;
        this.fanSharingPromptEnabled = !!this.selectedGameData?.team1?.fanSharingPromptEnabled;
        this.gamedayStoryPromptEnabled = !!this.selectedGameData?.team1?.gamedayStoryPromptEnabled;
        this.hideDownloadActions = this.selectedGameData?.team1?.hideDownload
          || this.selectedGameData?.team2?.hideDownload;
        this.showOrderPrintedPhoto = this.selectedGameData?.team1?.showOrderPrintedPhoto;
        this.showWatermark = this.selectedGameData?.team1?.showWatermark;
        this.enablePaidDownload = this.selectedGameData?.team1?.enablePaidDownload;
        this.syncTeamColorsFromSelectedGame();
        this.syncMlbLeagueFromSelectedGame();
      });

      return true;
    } catch (err) {
      console.warn(`[fetchGameData] ${err?.message ?? err}`);
      if (selectionStillCurrent()) {
        runInAction(() => {
          this.selectedGameData = null;
          this.gameDataIsFanLite = false;
        });
      }
      return false;
    }
  };

  private fetchTeamVisualAssets = async (options: { skipClearCache?: boolean } = {}): Promise<void> => {
    const path = AppServiceInstance.history?.location?.pathname
      || (typeof window !== "undefined" ? window.location.pathname : "");
    if (path === Routes.MY_MOMENTS || path === Routes.ALL_MOMENTO) {
      return;
    }

    if (this.teamsFetchPromise) {
      await this.teamsFetchPromise;
      return;
    }

    const run = async (): Promise<void> => {
      if (!options.skipClearCache) {
        this.clearCachedImages();
      }

      await this.loadCoreTeamLogos();
      await this.loadTeamAdsImages();

      if (!isMobileDevice()) {
        await this.loadTeamMarketingLogos();
      }
    };

    this.teamsFetchPromise = run().finally(() => {
      this.teamsFetchPromise = null;
    });

    await this.teamsFetchPromise;
  };

  /** @deprecated Prefer ensureTeamConfigLoaded + scheduleTeamVisualAssets */
  private fetchTeamsData = async (options: { skipClearCache?: boolean } = {}) => {
    if (!options.skipClearCache) {
      this.clearCachedImages();
    }
    await this.ensureTeamConfigLoaded();
    await this.fetchTeamVisualAssets({ skipClearCache: true });
  };

  private fetchSpecialSectionsData = async () => {
    if (this.sectionsFetchPromise) {
      return this.sectionsFetchPromise;
    }

    const run = async () => {
      let gameId = this.selectedGameData?._id?.toString();
      runInAction(() => {
        this.specialSectionsListLoading = true;
      });

      try {
        if (!gameId && this.selectedGame) {
          await this.ensureSelectedGameDataLoaded();
          gameId = this.selectedGameData?._id?.toString();
        }
        if (!gameId) {
          return;
        }

        const gameChanged = gameId !== this.lastSpecialSectionsGameId;
        if (gameChanged) {
          runInAction(() => {
            this.specialSections = [];
            this.allSuites = [];
            this.showSuites = false;
            this.availableSections = [];
          });
        }

        if (
          gameId === this.lastSpecialSectionsGameId
          && this.availableSections.length > 0
        ) {
          return;
        }

        const gameMongoId = this.selectedGameData?._id;
        if (!gameMongoId) {
          return;
        }

        const [allSections, optedOutSeats] = await Promise.all([
          getAllSections(gameMongoId),
          getOptedOutSeatsRequest(gameMongoId).catch(() => []),
        ]);
        const optedOutSet = new Set(
          (optedOutSeats || []).map((s) => `${(s.section || "").toLowerCase()}|${(s.row || "").toLowerCase()}|${(s.seat || "").toLowerCase()}`),
        );
        const sorted = (allSections || []).sort((item1, item2) => {
          const item1SectionType = (item1?.sectionType || "").toUpperCase();
          const item2SectionType = (item2?.sectionType || "").toUpperCase();
          const item1Name = (item1?.name || "").toUpperCase();
          const item2Name = (item2?.name || "").toUpperCase();
          if (item1SectionType < item2SectionType) {
            return -1;
          }

          if (item1SectionType === item2SectionType && item1Name < item2Name) {
            return -1;
          }

          if (item1SectionType === item2SectionType && item1Name === item2Name) {
            return 0;
          }

          return 1;
        });

        runInAction(() => {
          this.specialSections = sorted.filter((section) => section.isSpecialSection);
          this.allSuites = sorted.filter((section) => getSuiteName(section.name));
          this.showSuites = this.allSuites.length > 0;
          this.availableSections = sorted.map((section) => {
            const sectionName = section.name || "";
            const filteredSeats: { [key: string]: string[] } = {};
            const origSeats = section.seats || {};
            Object.keys(origSeats).forEach((row) => {
              const rowSeats = (origSeats[row] || []).filter((seat) => {
                const key = `${sectionName.toLowerCase()}|${(row || "").toLowerCase()}|${(seat || "").toLowerCase()}`;
                return !optedOutSet.has(key);
              });
              if (rowSeats.length > 0) {
                filteredSeats[row] = rowSeats;
              }
            });
            const rowsWithSeats = Object.keys(filteredSeats);
            const rows = (section.rows || []).filter((r) => rowsWithSeats.includes(r));
            return {
              ...section,
              rows: rows.length > 0 ? rows : section.rows || [],
              seats: filteredSeats,
            };
          });
          this.lastSpecialSectionsGameId = this.selectedGameData._id.toString();
        });
      } catch (err) {
        console.warn(`[fetchSpecialSectionsData] ${err?.message ?? err}`);
      } finally {
        runInAction(() => {
          this.specialSectionsListLoading = false;
        });
      }
    };

    this.sectionsFetchPromise = run().finally(() => {
      this.sectionsFetchPromise = null;
    });

    return this.sectionsFetchPromise;
  }

  private saveDataToWorkerStorage = async (
    entity: SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM,
    data: string,
  ) => {
    try {
      await axios.post(entity, { data });
    } catch (err) {
      console.log("^^^ The error above is expected xhr error");
    }
    // Safari: Cache API may not persist; always backup crop data to localStorage
    if (entity === SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA) {
      try {
        if (typeof localStorage !== "undefined") {
          localStorage.setItem(CROP_DATA_LOCALSTORAGE_KEY, data);
        }
      } catch {
        // localStorage unavailable (e.g. private mode)
      }
    }
  }

  private deleteDataFromWorkerStorage = async (entity: SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM) => {
    try {
      await axios.delete(entity);
    } catch (err) {
      console.log("^^^ The error above is expected xhr error");
    }
  }

  /** Team1/team2 scoreboard logos + header mark — highest priority for My Moments. */
  private loadCoreTeamLogos = async () => {
    if (!this.selectedGameData?.team1?.logo || !this.selectedGameData?.team2?.logo) {
      return;
    }

    const team1Src = this.selectedGameData.team1.logo;
    const team2Src = this.selectedGameData.team2.logo;
    if (
      team1Src === this.team1logoSrc
      && team2Src === this.team2logoSrc
      && this.team1logoDataUrl
      && this.team2logoDataUrl
      && this.team1logoImage?.width
      && this.team2logoImage?.width
    ) {
      this.syncTeamColorsFromSelectedGame();
      return;
    }

    this.syncTeamColorsFromSelectedGame();

    if (this.teamLogosLoading) return;
    this.teamLogosLoading = true;

    try {
      if (!(team1Src === this.team1logoSrc && this.team1logoDataUrl && this.team1logoImage?.width)) {
        const src = team1Src === this.team1logoSrc && this.team1logoDataUrl
          ? this.team1logoDataUrl : team1Src;
        try {
          const wantDataUrl = !isMobileDevice();
          const { image: team1Image, dataURL: team1DataURL } = await this.loadTeamLogo(src, wantDataUrl);
          if (team1Image?.width) {
            this.team1logoImage = team1Image;
            this.team1logoDataUrl = team1DataURL;
            this.team1logoSrc = team1Src;
          }
        } catch (err) {
          console.warn("Team 1 logo load failed, keeping previous:", err);
        }
      }

      if (!(team2Src === this.team2logoSrc && this.team2logoDataUrl && this.team2logoImage?.width)) {
        const src = team2Src === this.team2logoSrc && this.team2logoDataUrl
          ? this.team2logoDataUrl : team2Src;
        try {
          const wantDataUrl = !isMobileDevice();
          const { image: team2Image, dataURL: team2DataURL } = await this.loadTeamLogo(src, wantDataUrl);
          if (team2Image?.width) {
            this.team2logoImage = team2Image;
            this.team2logoDataUrl = team2DataURL;
            this.team2logoSrc = team2Src;
          }
        } catch (err) {
          console.warn("Team 2 logo load failed, keeping previous:", err);
        }
      }

      if (!this.mainLogoImage?.width) {
        const { image: mainImage } = await this.loadTeamLogo(LogoIcon, false);
        this.mainLogoImage = mainImage;
      }
    } finally {
      this.teamLogosLoading = false;
    }
  };

  /** Ticket ad + welcome popup — defer on mobile to avoid Safari tab crash. */
  private loadTeamMarketingLogos = async () => {
    if (!this.selectedGameData?.team1) return;

    const team1TicketAdSrc = this.selectedGameData.team1.ticketAdImage;
    if (!team1TicketAdSrc) {
      this.team1TicketAdImage = null;
      this.team1TicketAdDataUrl = null;
      this.team1TicketAdSrc = null;
    } else if (!(team1TicketAdSrc === this.team1TicketAdSrc && this.team1TicketAdDataUrl && this.team1TicketAdImage?.width)) {
      this.team1TicketAdImage = null;
      const src = team1TicketAdSrc === this.team1TicketAdSrc && this.team1TicketAdDataUrl
        ? this.team1TicketAdDataUrl : team1TicketAdSrc;
      const { image: team1TicketAdImage, dataURL: team1TicketAdDataURL } = await this.loadTeamLogo(src);
      this.team1TicketAdImage = team1TicketAdImage;
      this.team1TicketAdDataUrl = team1TicketAdDataURL;
      this.team1TicketAdSrc = team1TicketAdSrc;
    }

    const team1PopupSrc = this.selectedGameData.team1.popupImage;
    if (!team1PopupSrc) {
      this.team1PopupImage = null;
      this.team1PopupDataUrl = null;
      this.team1PopupSrc = null;
    } else if (
      (this.showPopupImage || !isMobileDevice())
      && !(team1PopupSrc === this.team1PopupSrc && this.team1PopupDataUrl && this.team1PopupImage?.width)
    ) {
      this.team1PopupImage = null;
      const src = team1PopupSrc === this.team1PopupSrc && this.team1PopupDataUrl
        ? this.team1PopupDataUrl : team1PopupSrc;
      const { image: team1PopupImage, dataURL: team1PopupDataURL } = await this.loadTeamLogo(src);
      this.team1PopupImage = team1PopupImage;
      this.team1PopupDataUrl = team1PopupDataURL;
      this.team1PopupSrc = team1PopupSrc;
    }
  };

  private loadTeamLogos = async () => {
    await this.loadCoreTeamLogos();
    await this.loadTeamMarketingLogos();
  };

  private loadTeamLogo = (src, getDataURL = true): Promise<ImageLoadResult> => {
    return loadImage(src, getDataURL);
  }

  private loadAdvertsment = async (src): Promise<Blob> => {
    const res = await fetch(src);
    const blob = await res.blob();
    return blob;

    // return URL.createObjectURL(blob);
  }

  // load all team advertisments metadata
  public loadTeamAdsMetadata = async (teamId?: string) => {
    const resolvedTeamId = teamId
      ?? this.selectedGameData?.team1?._id
      ?? this.welcomeTeamContext?._id;
    if (!resolvedTeamId) {
      this.teamAds = null;
      return;
    }

    const teamAds = await getTeamAdsByTeamId(resolvedTeamId) || {};
    this.teamAds = teamAds;
  }

  @action private loadWelcomeTeamHeaderLogo = async () => {
    const logoSrc = this.welcomeTeamContext?.logo;
    if (!logoSrc) return;

    if (
      logoSrc === this.team1logoSrc
      && this.team1logoDataUrl
      && this.team1logoImage?.width
    ) {
      return;
    }

    try {
      const wantDataUrl = !isMobileDevice();
      const { image, dataURL } = await this.loadTeamLogo(logoSrc, wantDataUrl);
      if (image?.width) {
        this.team1logoImage = image;
        this.team1logoDataUrl = dataURL;
        this.team1logoSrc = logoSrc;
      }
    } catch (err) {
      console.warn("Welcome team logo load failed:", err);
    }
  };

  public loadTeamPopupsMetadata = async () => {
    const teamId = this.selectedGameData?.team1?._id;
    if (!teamId) {
      this.teamPopupsConfig = null;
      this.teamPopupsFetchCompletedForTeamId = null;
      return;
    }

    try {
      const cfg = await getTeamPopupsByTeamId(teamId);
      this.teamPopupsConfig = cfg;
    } catch {
      this.teamPopupsConfig = null;
    } finally {
      this.teamPopupsFetchCompletedForTeamId = teamId;
    }
  }

  @action public requestTeamPopupForOccurrence = (occurrence: TeamPopupOccurrence) => {
    const teamId = this.selectedGameData?.team1?._id;
    if (!teamId || !this.teamPopupsConfig?.items?.length) {
      return;
    }
    if (occurrence === "my_moments_page_load") {
      try {
        const key = `momento_team_popup_my_moments_${teamId}`;
        if (typeof localStorage !== "undefined" && localStorage.getItem(key)) {
          return;
        }
      } catch {
        // ignore
      }
    }
    const noCreative: TeamPopupClickAction[] = ["feedback", "share_with_friends"];
    const item = this.teamPopupsConfig.items.find((i) => {
      if (i.occurrence !== occurrence) return false;
      if (noCreative.includes(i.clickAction)) return true;
      return !!i.imageUrl;
    });
    if (!item) {
      return;
    }
    this.pendingTeamPopup = item;
  }

  keepsakeDownloadPopupSessionKey = (gameId: string): string => (
    `keepsakeDownloadPopupShown:${gameId}`
  );

  isStillKeepsakeDownloadMode = (ticketViewMode?: string): boolean => (
    ticketViewMode === "Image"
    || ticketViewMode === "ImageScoreboard"
    || ticketViewMode === "Ticket1"
    || ticketViewMode === "Ticket2"
    || ticketViewMode === "Ticket3"
  );

  private isKeepsakeDownloadPopupShownThisVisit = (gameId: string): boolean => {
    try {
      return typeof sessionStorage !== "undefined"
        && !!sessionStorage.getItem(this.keepsakeDownloadPopupSessionKey(gameId));
    } catch {
      return false;
    }
  };

  willShowKeepsakeDownloadPopup = (ticketViewMode?: string): boolean => {
    if (this.hidePurchaseActions || !this.purchasePopupAfterDownloadedImage) {
      return false;
    }
    if (this.fanSharingPromptEnabled || this.gamedayStoryPromptEnabled) {
      return false;
    }
    if (!this.isStillKeepsakeDownloadMode(ticketViewMode)) {
      return false;
    }
    const gameId = this.selectedGameData?._id;
    if (!gameId || this.isKeepsakeDownloadPopupShownThisVisit(gameId)) {
      return false;
    }
    return true;
  };

  @action public requestKeepsakeDownloadPopupAfterSave = (ticketViewMode?: string): boolean => {
    if (!this.willShowKeepsakeDownloadPopup(ticketViewMode)) {
      return false;
    }
    this.pendingKeepsakeDownloadPopup = true;
    return true;
  };

  @action public markKeepsakeDownloadPopupShown = (): void => {
    const gameId = this.selectedGameData?._id;
    if (gameId) {
      try {
        sessionStorage.setItem(this.keepsakeDownloadPopupSessionKey(gameId), "1");
      } catch {
        // ignore
      }
    }
  };

  @action public dismissKeepsakeDownloadPopup = (): void => {
    this.markKeepsakeDownloadPopupShown();
    this.pendingKeepsakeDownloadPopup = false;
  };

  @action public clearPendingKeepsakeDownloadPopup = (): void => {
    this.pendingKeepsakeDownloadPopup = false;
  }

  fanSharingPromptSessionKey = (gameId: string): string => (
    `fanSharingPromptShown:${gameId}`
  );

  private isFanSharingPromptShownThisVisit = (gameId: string): boolean => {
    try {
      return typeof sessionStorage !== "undefined"
        && !!sessionStorage.getItem(this.fanSharingPromptSessionKey(gameId));
    } catch {
      return false;
    }
  };

  willShowFanSharingPrompt = (uploadFileType?: string): boolean => {
    if (!this.fanSharingPromptEnabled || this.gamedayStoryPromptEnabled
      || this.purchasePopupAfterDownloadedImage) {
      return false;
    }
    if (uploadFileType && uploadFileType !== "image") {
      return false;
    }
    const gameId = this.selectedGameData?._id;
    if (!gameId || this.isFanSharingPromptShownThisVisit(gameId)) {
      return false;
    }
    return true;
  };

  @action public requestFanSharingPromptAfterSave = (prepared?: {
    finalImageUrl: string;
    actualMimeType: string;
    uniqueFilename: string;
    uploadFileType?: string;
  }): boolean => {
    if (!this.willShowFanSharingPrompt(prepared?.uploadFileType)) {
      return false;
    }
    if (prepared?.finalImageUrl) {
      this.fanSharingPromptPrepared = {
        finalImageUrl: prepared.finalImageUrl,
        actualMimeType: prepared.actualMimeType,
        uniqueFilename: prepared.uniqueFilename,
      };
    }
    this.pendingFanSharingPrompt = true;
    return true;
  };

  @action public markFanSharingPromptShown = (): void => {
    const gameId = this.selectedGameData?._id;
    if (gameId) {
      try {
        sessionStorage.setItem(this.fanSharingPromptSessionKey(gameId), "1");
      } catch {
        // ignore
      }
    }
  };

  @action public dismissFanSharingPrompt = (): void => {
    this.markFanSharingPromptShown();
    this.pendingFanSharingPrompt = false;
    this.fanSharingPromptPrepared = null;
  };

  @action public clearPendingFanSharingPrompt = (): void => {
    this.pendingFanSharingPrompt = false;
    this.fanSharingPromptPrepared = null;
  }

  gamedayStoryPromptSessionKey = (gameId: string): string => (
    `gamedayStoryPromptShown:${gameId}`
  );

  private isGamedayStoryPromptShownThisVisit = (gameId: string): boolean => {
    try {
      return typeof sessionStorage !== "undefined"
        && !!sessionStorage.getItem(this.gamedayStoryPromptSessionKey(gameId));
    } catch {
      return false;
    }
  };

  willShowGamedayStoryPrompt = (uploadFileType?: string): boolean => {
    if (!this.gamedayStoryPromptEnabled || this.fanSharingPromptEnabled
      || this.purchasePopupAfterDownloadedImage) {
      return false;
    }
    if (uploadFileType && uploadFileType !== "image") {
      return false;
    }
    const gameId = this.selectedGameData?._id;
    if (!gameId || this.isGamedayStoryPromptShownThisVisit(gameId)) {
      return false;
    }
    return true;
  };

  @action public requestGamedayStoryPromptAfterSave = (prepared?: {
    uploadFileType?: string;
  }): boolean => {
    if (!this.willShowGamedayStoryPrompt(prepared?.uploadFileType)) {
      return false;
    }
    this.pendingGamedayStoryPrompt = true;
    return true;
  };

  @action public markGamedayStoryPromptShown = (): void => {
    const gameId = this.selectedGameData?._id;
    if (gameId) {
      try {
        sessionStorage.setItem(this.gamedayStoryPromptSessionKey(gameId), "1");
      } catch {
        // ignore
      }
    }
  };

  @action public dismissGamedayStoryPrompt = (): void => {
    this.markGamedayStoryPromptShown();
    this.pendingGamedayStoryPrompt = false;
  };

  @action public clearPendingTeamPopup = () => {
    const prev = this.pendingTeamPopup;
    this.pendingTeamPopup = null;
    if (prev?.occurrence === "my_moments_page_load") {
      const tid = this.selectedGameData?.team1?._id;
      if (tid) {
        try {
          localStorage.setItem(`momento_team_popup_my_moments_${tid}`, "1");
        } catch {
          // ignore
        }
      }
    }
  }

  public registerTeamPopupPurchaseHandler = (fn: (() => Promise<void>) | null) => {
    this.teamPopupDirectPurchaseHandler = fn;
  }

  // load all team advertisments
  public loadTeamAdsImages = async () => {
    if (!this.teamAds) {
      this.team1SponsorLogoImage = null;
      this.team1SponsorLogoDataUrl = null;
      this.team1SponsorLogoSrc = null;
      return;
    }

    const teamAds = this.teamAds || {};
    if (teamAds.titleSponsorLogoKey && teamAds.titleSponsorLogoImage) {
      const sponsorLogoSrc = teamAds.titleSponsorLogoImage;
      // check if image was already loaded
      if (!(sponsorLogoSrc === this.team1SponsorLogoSrc
          && this.team1SponsorLogoDataUrl
          && this.team1SponsorLogoImage?.width)) {
        // Don't clear before load - keep previous to avoid flickering
        // load from cached dataUrl if possible
        const src = sponsorLogoSrc === this.team1SponsorLogoSrc && this.team1SponsorLogoDataUrl
          ? this.team1SponsorLogoDataUrl : sponsorLogoSrc;
        try {
          const wantDataUrl = !isMobileDevice();
          const { image, dataURL } = await this.loadTeamLogo(src, wantDataUrl);
          if (image?.width) {
            this.team1SponsorLogoImage = image;
            this.team1SponsorLogoDataUrl = wantDataUrl ? dataURL : sponsorLogoSrc;
            this.team1SponsorLogoSrc = sponsorLogoSrc;
          }
        } catch (err) {
          console.warn("Sponsor logo load failed, keeping previous:", err);
        }
      }
    } else {
      this.team1SponsorLogoImage = null;
      this.team1SponsorLogoDataUrl = null;
      this.team1SponsorLogoSrc = null;
    }

    if (teamAds.adAfterContactInfoImage) {
      const adSrc = teamAds.adAfterContactInfoImage;
      // check if ad was already loaded
      if (!(adSrc === this.adAfterContactInfoSrc
          && this.adAfterContactInfoBlob)) {
        const blob = await this.loadAdvertsment(adSrc);
        this.adAfterContactInfoBlob = blob;
        this.adAfterContactInfoSrc = adSrc;
      }
    } else {
      this.adAfterContactInfoBlob = null;
      this.adAfterContactInfoSrc = null;
    }
  }

  // clear cached images (preserves team logos and sponsor logos so they stay visible until new ones load)
  private clearCachedImages = () => {
    // Keep teamAds and sponsor logos - loadTeamAdsMetadata/loadTeamAdsImages will replace when new ones load
    this.team1TicketAdImage = null;
    this.team1TicketAdDataUrl = null;
    this.team1TicketAdSrc = null;
    this.team1PopupImage = null;
    this.team1PopupDataUrl = null;
    this.team1PopupSrc = null;
    this.mainLogoImage = null;
    // Keep team1SponsorLogo* - loadTeamAdsImages will replace when new ones load

    this.adAfterContactInfoBlob = null;
    this.adAfterContactInfoSrc = null;
  }

  @action public setShowAdAfterContactInfo = (show: boolean) => {
    this.showAdAfterContactInfo = show;
  }

  public setMomentCameraData = (data: MomentResponseWithSize) => {
    this.defaultZoomWidth = data.defaultZoomWidth;
    this.defaultZoomHeight = data.defaultZoomHeight;
    this.centerCoords = data.relativeCoords;
    this.offsetY = data.offsetTop;
  };

  /** Drop camera defaults from the previous seat/section until the next moments fetch sets them. */
  @action public clearMomentCameraData = () => {
    this.defaultZoomWidth = null;
    this.defaultZoomHeight = null;
    this.centerCoords = null;
    this.offsetY = null;
  };

  /** All crop rows stored for the current seat (global + per-moment + per-momento). */
  public exportCropEntriesForCurrentSeat = (): Record<string, ImageCropData> => {
    if (!this.storageCropData) return {};
    const prefix = this.generateCropDataKey();
    const out: Record<string, ImageCropData> = {};
    for (const key of Object.keys(this.storageCropData)) {
      if (key === prefix || key.startsWith(`${prefix}_`)) {
        const v = this.storageCropData[key] as ImageCropData;
        out[key] = { ...v };
      }
    }
    return out;
  };

  @action public mergeCropSnapshotForSeat = (
    seatKey: string,
    entries: Record<string, ImageCropData>,
  ): boolean => {
    if (this.generateCropDataKey() !== seatKey) return false;
    this.storageCropData = {
      ...(this.storageCropData || {}),
      ...entries,
    };
    this.saveDataToWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.CROP_DATA,
      JSON.stringify(this.storageCropData),
    );
    return true;
  };

  public exportStorageCameraSnapshot = (): {
    defaultZoomWidth: number | null;
    defaultZoomHeight: number | null;
    centerCoords: Coords | null;
    offsetY: number | null;
  } => ({
    defaultZoomWidth: this.defaultZoomWidth,
    defaultZoomHeight: this.defaultZoomHeight,
    centerCoords: this.centerCoords,
    offsetY: this.offsetY,
  });

  @action public applyStorageCameraSnapshot = (snap: {
    defaultZoomWidth: number | null;
    defaultZoomHeight: number | null;
    centerCoords: Coords | null;
    offsetY: number | null;
  } | null): void => {
    if (!snap) return;
    this.defaultZoomWidth = snap.defaultZoomWidth;
    this.defaultZoomHeight = snap.defaultZoomHeight;
    this.centerCoords = snap.centerCoords;
    this.offsetY = snap.offsetY;
  };

  @action public setResumeSessionDialogWasShown = (value: boolean) => {
    this.resumeSessionDialogWasShown = value;
  }

  /** Wipes all state and persistent storage for sign-out. Call before navigating to games. */
  @action public resetAllStateForSignOut = async (): Promise<void> => {
    // Delete all worker storage (Cache API)
    const allKeys = Object.values(SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM);
    await Promise.all(allKeys.map((k) => this.deleteDataFromWorkerStorage(k)));

    clearMomentoSiteData();

    // Reset observables
    this.selectedGame = null;
    this.selectedGameData = null;
    this.lastSpecialSectionsGameId = null;
    this.specialSections = [];
    this.allSuites = [];
    this.selectedSection = null;
    this.selectedSpecialSection = null;
    this.selectedRow = null;
    this.selectedSeat = null;
    this.selectedSuite = null;
    this.selectedSectionIsSpecial = null;
    this.selectedSectionType = null;
    this.selectedPlaceX = null;
    this.selectedPlaceY = null;
    this.selectedPlaceWidth = null;
    this.selectedPlaceHeight = null;
    this.selectedFirstName = null;
    this.selectedLastName = null;
    this.selectedEmail = null;
    this.selectedCustomerId = null;
    this.selectedRegistrationUserId = null;
    this.registrationSessionKey = null;
    this.selectedPhone = null;
    this.selectedZipCode = null;
    this.selectedBirthDate = null;
    this.selectedCountry = null;
    this.selectedShowMomentCaption = false;
    this.storageCropData = null;
    this.storageSelectedPlaceData = null;
    this.storageSelectedMomentosData = null;
    this.selectedTeamOptIn = null;
    this.selectedSponsorOptIn = null;
    this.selectedThirdOptIn = null;
    this.selectedWatermarkOptInShown = null;
    this.selectedWatermarkOptIn = null;
    this.selectedIsSeasonTicketHolder = false;
    this.isReturningFanAuthenticated = false;
    this.returningFanPromptHandledForGame = null;
    this.selectedMoment = null;
    this.selectedMomento = null;
    this.shownTipsData = null;
    this.availableSections = [];
    this.stadiumMap = new Map();
    this.lastPlaces = [];
    this.resumeSessionDialogWasShown = false;
    this.selectedVerifyLastName = null;
    this.clearCachedImages();
    this.team1logoImage = null;
    this.team1logoDataUrl = null;
    this.team1logoSrc = null;
    this.team2logoImage = null;
    this.team2logoDataUrl = null;
    this.team2logoSrc = null;
    this.team1SponsorLogoImage = null;
    this.team1SponsorLogoDataUrl = null;
    this.team1SponsorLogoSrc = null;
    this.teamAds = null;
    this.teamPopupsConfig = null;
    this.teamPopupsFetchCompletedForTeamId = null;
    this.pendingTeamPopup = null;
    this.pendingKeepsakeDownloadPopup = false;
    this.purchasePopupAfterDownloadedImage = false;
    this.pendingFanSharingPrompt = false;
    this.fanSharingPromptEnabled = false;
    this.fanSharingPromptPrepared = null;
    this.pendingGamedayStoryPrompt = false;
    this.gamedayStoryPromptEnabled = false;
    this.teamPopupDirectPurchaseHandler = null;
    this.showAdAfterContactInfo = true;
    this.adAfterContactInfoBlob = null;
    this.adAfterContactInfoSrc = null;
    this.defaultZoomWidth = null;
    this.defaultZoomHeight = null;
    this.centerCoords = null;
    this.offsetY = null;
  }

  /**
   * Surname verification after seat selection: team Safeguard and/or per-game flag.
   */
  public requiresSurnameVerification = (): boolean => {
    const game = this.selectedGameData;
    const team1 = game?.team1 as TeamGamePopulated | undefined;
    const under18On = team1?.under18FlowEnabled === true
      || this.welcomeTeamContext?.under18FlowEnabled === true;
    if (under18On && isUnder18FlowTeenSession()) return true;
    if (!game) return false;
    if (game.checkLastName) return true;
    return team1?.surnameVerificationEnabled === true;
  };

  public hasCompletedSurnameVerification = (): boolean => (
    !!(this.selectedVerifyLastName && this.selectedVerifyLastName.trim())
  );

  /** True when the fan still needs the surname step (feature on and not yet verified). */
  public shouldShowSurnameVerificationStep = (): boolean => (
    this.requiresSurnameVerification() && !this.hasCompletedSurnameVerification()
  );

  @action public clearVerifyLastName = () => {
    this.selectedVerifyLastName = null;
    this.deleteDataFromWorkerStorage(
      SERVICE_WORKER_STORAGE_ENDPOINTS_ENUM.VERIFY_LAST_NAME,
    );
  };

  /** True when operator has configured the team with no user-detail fields to collect */
  public getHasNoUserDetailsFieldsToCollect = (): boolean => {
    const team1 = this.selectedGameData?.team1 ?? this.welcomeTeamContext;
    const udc = team1?.userDetailsConfig;
    if (!udc) return false;
    const fields = ["showFirstName", "showLastName", "showEmail", "showPhoneField", "showZipCodeField", "showCountryField", "showDateOfBirth", "showSeasonTicketHolder"] as const;
    return fields.every((k) => !udc[k]);
  };

  @action setFanFlowRequiresTeamPicker = (value: boolean) => {
    this.fanFlowRequiresTeamPicker = value;
  };

  @action applyWelcomeTeam = (team: TeamPopulated) => {
    this.welcomeTeamContext = {
      _id: team._id,
      name: team.name,
      logo: team.logo,
      primaryColor: team.primaryColor,
      userDetailsConfig: team.userDetailsConfig,
      customTeamOptInText: team.customTeamOptInText,
      customSponsorOptInText: team.customSponsorOptInText,
      customThirdOptInText: team.customThirdOptInText,
      customFanIdOptInText: team.customFanIdOptInText,
      customConsentText: team.customConsentText,
      profileEnabled: team.profileEnabled,
      leadGenFirst: team.leadGenFirst,
      fanWelcomeRequiresClubPicker: team.fanWelcomeRequiresClubPicker,
      momentScoreboardHideTeam1Logo: team.momentScoreboardHideTeam1Logo,
      homeTeamOnTop: team.homeTeamOnTop === true,
      teamRegion: team.teamRegion,
      leagueNames: leagueNamesFromTeamLeague(team.league),
      menuBarOptOutEnabled: team.menuBarOptOutEnabled === true,
      analyticsTrackingEnabled: team.analyticsTrackingEnabled === true,
      analyticsPopupConfig: team.analyticsPopupConfig
        ? sanitizeAnalyticsPopupConfig(team.analyticsPopupConfig)
        : undefined,
      cookiePolicyText: team.cookiePolicyText || "",
      termsOfUseText: team.termsOfUseText || "",
      privacyPolicyText: team.privacyPolicyText || "",
      seasonLongOptOutEnabled: team.seasonLongOptOutEnabled === true,
      surnameVerificationEnabled: team.surnameVerificationEnabled === true,
      under18FlowEnabled: team.under18FlowEnabled === true,
      preMomentSignup: team.preMomentSignup,
    } as TeamGamePopulated;
    this.showUserDetailsFirst = team.leadGenFirst === true;
    if (team.primaryColor) {
      this.team1Color = team.primaryColor;
    }
    if (team.fanWelcomeRequiresClubPicker) {
      this.setFanFlowRequiresTeamPicker(true);
    }
    if (team.leadGenFirst) {
      this.ensureWelcomeHeaderBrandAssets().catch(() => undefined);
    }
    try {
      sessionStorage.setItem(
        "momento_welcome_team_context",
        JSON.stringify(this.welcomeTeamContext),
      );
    } catch {
      /* ignore */
    }
  };

  @action setHubSelectedHomeTeamName = (teamDisplayName: string, teamId?: string) => {
    const n = teamDisplayName.trim().toLowerCase();
    this.hubSelectedHomeTeamName = n;
    const id = teamId ? String(teamId).trim() : "";
    this.hubSelectedHomeTeamId = id || null;
    try {
      sessionStorage.setItem("momento_hub_team_filter", n);
      if (id) {
        sessionStorage.setItem("momento_hub_team_id", id);
      } else {
        sessionStorage.removeItem("momento_hub_team_id");
      }
    } catch {
      /* ignore */
    }
  };

  public getNextPage = (id: string) => {
    const skipUserDetails = this.getHasNoUserDetailsFieldsToCollect();
    switch (id) {
      case Routes.WELCOME:
        if (this.showUserDetailsFirst) return Routes.DETAILS;
        return this.fanFlowRequiresTeamPicker ? Routes.TEAMS : Routes.GAMES;
      case Routes.TEAMS:
        return Routes.GAMES;
      case Routes.GAMES:
        return Routes.SEAT;
      case Routes.SEAT:
        if (this.selectedSectionIsSpecial) {
          return Routes.PLACE;
        }
        if (this.shouldShowSurnameVerificationStep()) {
          return Routes.VERIFY_SEAT;
        }
        if (this.selectedCustomerId) {
          return Routes.MY_MOMENTS;
        }
        if (skipUserDetails) return Routes.MY_MOMENTS;
        return this.showUserDetailsFirst ? Routes.MY_MOMENTS : Routes.DETAILS;
      case Routes.PLACE:
        if (this.shouldShowSurnameVerificationStep()) {
          return Routes.VERIFY_SEAT;
        }
        if (this.selectedCustomerId) {
          return Routes.MY_MOMENTS;
        }
        if (skipUserDetails) return Routes.MY_MOMENTS;
        return this.showUserDetailsFirst ? Routes.MY_MOMENTS : Routes.DETAILS;
      case Routes.DETAILS:
        if (this.showUserDetailsFirst) {
          return this.fanFlowRequiresTeamPicker ? Routes.TEAMS : Routes.GAMES;
        }
        return Routes.MY_MOMENTS;
      case Routes.VERIFY_SEAT:
        if (this.selectedCustomerId) {
          return Routes.MY_MOMENTS;
        }
        if (skipUserDetails) return Routes.MY_MOMENTS;
        if (this.showUserDetailsFirst) return Routes.MY_MOMENTS;
        return Routes.DETAILS;
      default:
    }

    return id;
  }

  // performs necessary saving and moves router to the next page
  @action public moveToTheNextPage = (id: string) => {
    const nextPageId = this.getNextPage(id);
    if (AppServiceInstance.history?.push) {
      AppServiceInstance.history.push(nextPageId);
    } else {
      window.location.href = nextPageId;
    }
  }

  private getPreviousPage = (id: string) => {
    switch (id) {
      case Routes.GAMES:
        if (this.showUserDetailsFirst) return Routes.DETAILS;
        return this.fanFlowRequiresTeamPicker ? Routes.TEAMS : Routes.WELCOME;
      case Routes.TEAMS:
        return this.showUserDetailsFirst ? Routes.DETAILS : Routes.WELCOME;
      case Routes.ENHANCED_GA_MAP:
        return Routes.ENHANCED_GA_AREA;
      case Routes.ENHANCED_GA_AREA:
        return Routes.ENHANCED_GA_WHERE;
      case Routes.ENHANCED_GA_WHERE:
        return Routes.GAMES;
      case Routes.SEAT:
        if (this.enhancedGaNormalSeatFromFlow) {
          return Routes.ENHANCED_GA_WHERE;
        }
        return Routes.GAMES;
      case Routes.PLACE: {
        const egaTeam = this.shouldUseEnhancedGeneralAdmissionFlow();
        const gaCropFlow = !this.enhancedGaNormalSeatFromFlow
          && (
            this.enhancedGaSessionActive
            || (
              egaTeam
              && (this.selectedSectionIsSpecial === true || this.isGaMomentContext())
            )
          );
        if (gaCropFlow) {
          return Routes.ENHANCED_GA_MAP;
        }
        return Routes.SEAT;
      }
      case Routes.DETAILS:
        if (this.showUserDetailsFirst) {
          return Routes.WELCOME;
        }
        if (this.shouldShowSurnameVerificationStep()) {
          return Routes.VERIFY_SEAT;
        }
        return this.selectedSectionIsSpecial ? Routes.PLACE : Routes.SEAT;
      case Routes.VERIFY_SEAT:
        return this.selectedSectionIsSpecial ? Routes.PLACE : Routes.SEAT;
      case Routes.MY_MOMENTS:
      case Routes.MOMENT:
        if (this.shouldShowSurnameVerificationStep()) {
          // Prefer details when they would have been collected after verify
          if (
            !this.selectedCustomerId
            && !this.isReturningFanAuthenticated
            && !this.showUserDetailsFirst
            && !this.getHasNoUserDetailsFieldsToCollect()
          ) {
            return Routes.DETAILS;
          }
          return Routes.VERIFY_SEAT;
        }
        if (this.selectedCustomerId
          || this.isReturningFanAuthenticated
          || this.showUserDetailsFirst
          || this.hasCompletedSurnameVerification()
        ) {
          return (this.selectedSectionIsSpecial === true || this.isGaMomentContext())
            ? Routes.PLACE
            : Routes.SEAT;
        }
        return Routes.DETAILS;
        break;
      default:
    }

    return id;
  }

  // moves router to the previous page
  @action public moveToThePreviousPage = (id: string) => {
    const previousId = this.getPreviousPage(id);
    AppServiceInstance.history.push(previousId);
  }

  // indicates that popup image was shown on this session
  @action public teamPopupWasShown = () => {
    this.popupImageWasShown = true;
    this.showPopupImage = false;
  }
}

// add fetch method

export const StorageServiceInstance = new StorageService();

export const StorageServiceContext = createContext(StorageServiceInstance);
